/**
 * admin.en — the English staff-facing admin catalog, and the content source
 * of truth for this namespace: every value below is verbatim from
 * `packages/views/src/{events,locations,members}/**`,
 * `web/src/admin/**`, and the sync-layer toast strings in
 * `web/src/**\/*.sync.ts` (events/ticket-types/locations/members).
 * `admin.de.ts` is its Sie translation (matching the auth catalog's own Sie
 * register), composed into the shared `en` object by `messages.en.ts`.
 *
 * `admin.common.*` holds the handful of generic action words
 * (Cancel/Delete/Save/…) reused byte-identically across many forms; every
 * other key sits under the feature that owns it. A `One`/`Other`/`Zero` key
 * pair covers a count-driven word choice the source renders as a ternary.
 */
export const admin = {
	site: {
		mapsJavascript: "Maps JavaScript API",
		placesNew: "Places API (New)",
		geocoding: "Geocoding API",
		staticMaps: "Maps Static API",

		manualAddress:
			"Address suggestions need Google Maps setup. You can enter the address manually.",
		configureMaps: "Configure Google Maps",
		appearance: "Website appearance",
		maps: "Google Maps",
		configure: "Configure",
		manage: "Manage",
		configured: "Configured",
		notConfigured: "Not configured",
		checked: "Address search checked",
		imageInvalid: "Choose a PNG, JPEG or WebP smaller than 2 MB.",
		mapsError:
			"Google could not verify address search. Check billing, enabled APIs and the website restrictions in the guide below. Your saved key was not changed.",
		saveError: "Changes could not be saved. Please try again.",
		testAddress: "Test address",
		saveTest: "Save & test",
		close: "Close",
		searchSharing: "Search & shared links",
		title: "Title",
		titleHint: "Uses your organization name when left blank.",
		description: "Description",
		sharingImage: "Sharing image",
		chooseImage: "Choose image",
		siteIcon: "Site icon",
		change: "Change",
		browserTab: "Browser tab",
		homeScreen: "Home screen",
		saveAppearance: "Save appearance",
		searchResult: "Search result",
		sharedLink: "Shared link",
		preview: "Preview",
		browserKey: "Browser key",
		apiKey: "API key",
		guide: "Get an API key",
		openCloud: "Open Google Cloud",
		projectStep:
			"and choose a project. Keep that project selected for every step below.",
		openBilling: "Open Billing",
		billingStep:
			"and link a billing account to that project. Google charges for Maps usage.",
		enableStep: "Open each API and press Enable:",
		openKeys: "Open Keys & Credentials",
		createStep: "Choose Create credentials → API key.",
		websitesStep:
			"Under Application restrictions, choose Websites. Add your admin website:",
		restrictStep:
			"Under API restrictions, choose Restrict key. Select the same four APIs from step 3 and save.",
		pasteStep: "Copy the key into API key above, then press Save & test.",
		restriction: "Admin website restriction",
		saved: "Changes saved",
		siteIconPreviews: "Site icon previews",
		changeIcon: "Change site icon",
	},
	notifications: {
		title: "Notifications",
		setupPasskey: "Set up a passkey",
	},
	domains: {
		checkingPublicDns: "Checking public DNS…",
		dnsRecordAbsent: "Absent from public DNS",
		dnsLookupUnavailable: "DNS lookup unavailable",
		dnsRecordMatches: "Found in public DNS",
		dnsRecordNeedsChange: "Not matching public DNS",
		publicDnsCheckedAt: "Public DNS checked at {{time}}",
		hostingDnsCheckedAt: "Firebase last checked at {{time}}",
		providerUnavailable: "The provider could not be reached. We will retry.",
		shopNotReached: "We cannot reach this shop yet. We will retry.",
		popupBlocked: "Allow pop-ups for this page, then test again.",
		browserDelayed: "The shop has not replied. Return here and try again.",
		addAnother: "Connect another domain",
		dnsRouting: "DNS routing",
		dnsAction: "Update your DNS records",
		ownershipAction: "First, verify your domain",
		ownershipInstructions:
			"Add this TXT record at your DNS provider. It proves ownership; it does not connect your website yet.",
		nextDnsInstructions:
			"Next, we’ll show the records that point your domain to your shop. The secure certificate and shop checks follow.",
		hostInstructions:
			"If your provider adds the DNS zone automatically, enter only the part before that zone. Check your provider’s zone before copying a host value.",
		hostingInstructions:
			"Update these records at your DNS provider to connect and secure your shop.",
		preparingRecords:
			"Preparing your DNS instructions. We’ll check automatically.",
		preparingDnsTitle: "Preparing DNS instructions",
		waitingSecure:
			"DNS instructions are complete. We’re checking the secure connection and shop access.",
		lastCheck: "Last check: {{status}}",
		autoPrimary:
			"This becomes your primary shop address when all connection checks pass.",
		checkForPrimary:
			"Check the connection to make this your primary address when it is ready.",
		keepPrimary:
			"Your current primary shop address stays in use until you choose a replacement.",
		browserPending: "Connected — test before making primary",
		addRecord: "Add record",
		checkingConnection: "Checking connection…",
		checkComplete: "Check complete",
		checkDelayed: "Still checking. You can retry shortly.",
		title: "Custom domain",
		close: "Close",
		manage: "Manage",
		connectYour: "Connect your domain",
		yourDomain: "Your domain",
		connect: "Connect domain",
		primaryShop: "Primary shop address",
		platformAddress: "Platform address",
		openShop: "Open shop",
		newTab: "opens in a new tab",
		browserPassed: "Connected — browser check passed",
		instruction:
			"Open your domain provider’s DNS settings and create these records. Click a name or value to copy it.",
		removeRecord: "Remove this record",
		type: "Type",
		name: "Name",
		fullRecordName: "Full DNS name",
		rootHostHint: "If your DNS zone is {{hostname}}, enter this in Host:",
		hostField: "host field",
		value: "Value",
		recordName: "record name",
		recordValue: "record value",
		checkShop: "Check your shop",
		primary: "Primary address",
		makePrimary: "Use as primary",
		checkConnection: "Check connection",
		disconnect: "Disconnect",
		disconnectQuestion: "Disconnect {{hostname}}?",
		platformStays: "Your shop stays available at its platform address.",
		keep: "Keep domain",
		disconnectDomain: "Disconnect domain",
		checks: "Connection checks",
		ownership: "Your ownership",
		verified: "Verified",
		waitingRecord: "Waiting for your record",
		secure: "Secure connection",
		ready: "Ready",
		preparing: "Preparing",
		shopAccess: "Shop access",
		configured: "Configured",
		browserCheck: "Browser check",
		passedHere: "Passed in this browser",
		notChecked: "Not checked yet",
		invalid:
			"Enter your domain, like shop.example.com — without https:// or a page address.",
		removing: "Removing the connection",
		cleanupFailed: "The connection could not be removed. Try again.",
		retryCleanup: "Retry removal",
		disconnecting: "Disconnecting…",
		unavailable: "Domain unavailable",
		accessChanged: "Access changed. Ask an organization owner.",
		adminAttention: "Connection needs administrator attention",
		limit: "Domain limit reached. Contact your administrator.",
		waitingRecords: "Waiting for your records",
		preparingSecure: "Preparing your secure connection",
		finishing: "Finishing your connection",
		connected: "Connected",
		loadFailed: "Could not load your domains. Reopen Integrations to retry.",
		saveFailed: "Could not save your request. Try again.",
		browserFailed:
			"Your shop could not complete the browser check. Try again after a few minutes.",
		checkingShop: "Checking your shop…",
		testSuccess:
			"{{name}}: your shop works in this browser. You can return to settings.",
		testFailed:
			"Your shop could not complete the browser check. Return to settings and try again after a few minutes.",
		signIn: "Sign in to connect a domain.",
	},
	organizations: {
		label: "Organization",
		choose: "Choose an organization",
		reading: "Loading your organizations",
		openedElsewhere: "Opened {{name}}, chosen in another window.",
		loadFailed: "Could not load your organizations.",
		switchFailed: "Could not switch organizations. Try again.",
		switchTo: "Switch to",
		invited: "You've been invited",
		invitedTo: "Invited to",
		search: "Search organizations",
		noMatch: 'No organizations match "{{query}}"',
		notConnected: "This shop address is not connected.",
		notAvailable: "This shop address is not available. Try again later.",
		invalid: "This organization address is invalid.",
	},
	// An invitation announced to a signed-in person, answered on the
	// organization entry and in the chooser. `roleName` is the organization's
	// own catalog name, never translated.
	invitations: {
		by: "{{invitedBy}} invited you as {{roleName}}.",
		cost: "Members will see your name and email.",
		accept: "Accept",
		decline: "Decline",
		declineConfirm: "Yes, decline",
		keep: "Keep it",
		declineCost: "Only {{invitedBy}} can invite you again.",
		acceptAria: "Accept — {{name}}",
		declineAria: "Decline — {{name}}",
		keepAria: "Keep it — {{name}}",
		failed: "That didn't go through — nothing has changed.",
		loadFailed: "Could not check your invitations.",
		// A founding invitation's consent: the operator's own documents,
		// linked from the sign-in consent's own sentence frames.
		platformTerms: "platform terms",
		platformPrivacy: "platform privacy notice",
		// One entry per InvitationRefusal: what the server said, and the way back.
		refused: {
			"no-invitation": {
				said: "No invitation here for this address.",
				remedy: "Ask {{invitedBy}} to invite you again.",
			},
			"invite-unreadable": {
				said: "Something is wrong with this invitation.",
				remedy: "Ask {{invitedBy}} to check it.",
			},
			"invite-redeemed": {
				said: "You're already a member.",
				remedy: "Open it from your organizations.",
			},
			"invite-declined": {
				said: "You already declined.",
				remedy: "Ask {{invitedBy}} to invite you again.",
			},
			"invite-revoked": {
				said: "This invitation was withdrawn.",
				remedy: "Ask {{invitedBy}} to invite you again.",
			},
			"invite-expired": {
				said: "This invitation has expired.",
				remedy: "Ask {{invitedBy}} to invite you again.",
			},
			"platform-terms": {
				said: "Accepting needs the current platform terms.",
				remedy: "Reload the page to read them.",
			},
		},
	},
	common: {
		confirm: "Confirm",
		cancel: "Cancel",
		delete: "Delete",
		save: "Save",
		saveChanges: "Save changes",
		retrySaveShort: "Retry",
		retrySave: "Failed · Save again",
		retryDelete: "Failed · Delete again",
		retrySend: "Failed · Send again",
		retry: "Failed · Try again",
		discard: "Discard",
		close: "Close",
		keepEditing: "Keep editing",
		confirmCannotUndo: "This action cannot be undone.",
		// A refusal turns on existence inside the deleting transaction, while
		// the numbers are count() aggregations read afterwards — a failed
		// aggregation degrades the message, never the guard, so an all-zero
		// census is a real state <delete-refusal> has to say out loud.
		deleteRefusalCountsUnknown:
			"The exact numbers couldn't be read just now — but whatever is blocking the deletion is still there.",
		status: {
			draft: "Draft",
			published: "Published",
			archived: "Archived",
		},
	},
	events: {
		list: {
			countSingular: "event",
			countPlural: "events",
			addAriaLabel: "Add event",
			loadingAriaLabel: "Loading events",
			errorTitle: "Could not load events",
			errorBody:
				"Check your connection — the event list will reload automatically.",
			deniedTitle: "You don't have access to the event list",
			deniedBody:
				"Ask an admin to grant you a role with event-management access.",
			emptyTitle: "No events yet",
			emptyBody:
				"Add the first one — a name, the dates, and one of your locations as the venue.",
			addFirst: "Add your first event",
			soldOfCapacity: "{{sold}} of {{capacity}} sold",
			soldCaption: "/ {{capacity}} sold",
		},
		tile: {
			ariaLabel: "{{name}}, {{status}}",
			ariaLabelInteractive: "{{name}}, {{status}}, open event details",
		},
		form: {
			moveToDayFailed:
				"{{day}} skips those hours when the clocks go forward, so the entry window cannot move there. Pick another day.",
			sectionBasics: "Basics",
			sectionScheduleVenue: "Schedule & venue",
			sectionTicketTypes: "Ticket types",
			sectionTicketTemplate: "Ticket look",
			ticketTemplateLabel: "Ticket template",
			useDefaultTemplate: "Use the default look",
			logoLabel: "Event logo",
			logoHint:
				"Optional — the event's own wordmark, drawn on tickets that support it. Without one, a wordmark is generated from the event name.",
			logoChooseButton: "Upload logo",
			logoChangeButton: "Change logo",
			logoRemoveButton: "Remove",
			logoUnsupportedTypeError: "Only JPG, PNG, or WEBP images are supported.",
			logoTooLargeError:
				"This image is larger than 2 MB — choose a smaller file.",
			sectionArtwork: "Artwork",
			sectionStatusLimits: "Status & limits",
			nameLabel: "Event name",
			nameHint: "What guests see first — keep it short and memorable.",
			descriptionLabel: "Description",
			descriptionHint: "Optional — a line or two about the event.",
			schedulePlaceholder: "Pick when your event happens",
			venueLabel: "Venue",
			venuePlaceholder: "Pick a venue",
			addTicketTypeAriaLabel: "Add ticket type",
			noTicketTypesYet: "No ticket types yet.",
			ticketTypeShowsAfter: 'Shows after "{{name}}" sells out',
			priceDescendsWarning:
				"Priced lower than the ticket it follows — check this is intended.",
			predecessorClosesAtWarning:
				"Note: the ticket it follows has its own sales end date — once that passes, this one appears even if the other hasn't sold out.",
			noTicketTypesHint:
				"Every event needs at least one ticket type — add your first.",
			addFirstTicketType: "Add your first ticket type",
			draftTicketTypeMeta: "{{kind}} · {{price}} · {{capacity}} capacity",
			removeTicketTypeAriaLabel: "Remove {{name}}",
			duplicateTicketTypeAriaLabel: "Duplicate {{name}}",
			reorderTicketTypeAriaLabel: "Drag to reorder {{name}}",
			reorderGrabbed:
				"{{name}} grabbed, position {{position}} of {{total}}. Use the arrow keys to move it, space to drop, escape to cancel.",
			reorderMoved: "{{name}}, position {{position}} of {{total}}.",
			reorderDropped: "{{name}} dropped at position {{position}} of {{total}}.",
			reorderCancelled: "{{name}} returned to its original position.",
			artworkOptionalHint: "Optional, but it's the first thing people notice.",
			generateWithAi: "Generate with AI",
			chooseDifferentWayAriaLabel: "Choose a different way to add artwork",
			describePosterLabel: "Describe the poster",
			describePosterPlaceholder:
				"e.g. neon rooftop rave, warm analog film grain",
			describePosterHint:
				"A mood, a color palette, a scene — a few words is enough.",
			generate: "Generate",
			generating: "Generating…",
			posterConceptsLabel: "poster concepts",
			changeArtwork: "Change artwork",
			removeArtwork: "Remove",
			statusLabel: "Status",
			publishNow: "Publish this event now",
			publishNowHint:
				"Off keeps it as a draft, visible only to your team — you can publish anytime after.",
			maxTicketsLabel: "Max tickets per order",
			maxTicketsHint: "The most tickets a single buyer can add to one order.",
			previewLabel: "Preview",
			deleteConfirmTitle: "Delete this event?",
			publishNoTicketTypesTitle: "No ticket types yet",
			publishNoTicketTypesMessage:
				"This event has no ticket types — no one will be able to buy a ticket until you add one. Publish anyway?",
			publishAnyway: "Publish anyway",
			addTicketTypeFirstCancelText: "Add a ticket type first",
			saveFailed: "Could not save the event — try again.",
			posterSaveFailed:
				"Could not save the artwork — it will be saved with the rest of the event.",
			deleteFailed: "Could not delete the event — try again.",
			// <delete-refusal>'s event copy. Archive is the real alternative —
			// Event.status already carries 'archived' — and an order only ever
			// goes one way, one at a time, from the order sheet's own purge.
			deleteRefusalTitle: "This event can't be deleted",
			deleteRefusalLead:
				"Orders and tickets still belong to it, and deleting it would leave them without an event:",
			deleteRefusalOrdersOne: "1 order was placed for it",
			deleteRefusalOrdersOther: "{{count}} orders were placed for it",
			// One remedy clause per census row, rendered under the row it
			// answers — a remedy naming what this refusal did not report sends
			// the operator hunting for something the census just proved absent.
			deleteRefusalOrdersRemedy:
				"If those are test orders, erase them one at a time from the order list — that's the only way an order is ever removed.",
			deleteRefusalTicketsOne: "1 ticket was issued for it",
			deleteRefusalTicketsOther: "{{count}} tickets were issued for it",
			deleteRefusalTicketsRemedy:
				"A ticket is only removed when its order is erased — never on its own.",
			deleteRefusalCheckoutsOne: "1 checkout is still running",
			deleteRefusalCheckoutsOther: "{{count}} checkouts are still running",
			deleteRefusalCheckoutsRemedy:
				"A checkout that's still running ends on its own in a few minutes — nothing to do.",
			deleteRefusalCampaignsOne: "1 campaign is still running for it",
			deleteRefusalCampaignsOther:
				"{{count}} campaigns are still running for it",
			// The only row here whose blocker costs money while it stands, and
			// the only one whose remedy is a gesture the operator has to make:
			// a checkout retires itself and an order is erased somewhere else,
			// while a campaign goes on spending until it is ended.
			deleteRefusalCampaignsRemedy:
				"A campaign that's still running has to be ended under Promotion first — otherwise Meta keeps spending on an ad that points at a page that no longer exists.",
			deleteRefusalRemedy:
				"Archive it instead: an archived event disappears from the shop, while every order, ticket and invoice stays intact.",
			// The event editor's refund card. `refundableTypeIds` is a closed
			// whitelist, so `typesNotRefundable` / `typesNoneSelected` /
			// `futureTypes` each state one half of its hidden consequence out
			// loud rather than leaving it to be inferred from empty boxes.
			refund: {
				section: "Returns",
				toggleOn: "Returns are open",
				toggleOff: "No returns for this event",
				offHint: "Default: no returns.",
				windowLabel: "Return period",
				windowPlaceholder: "From when to when",
				paidBeforeToggle: "Only orders bought before a set time",
				paidBeforeOffHint:
					"When the order was bought doesn't matter — only the return period decides.",
				paidBeforeLabel: "Bought before",
				paidBeforeHint:
					"Anyone who bought at that moment or later gets nothing back.",
				messageLabel: "What buyers read on the return page",
				messageHintDe: "Deutsch",
				messageHintEn: "English",
				messagePlaceholder:
					"e.g. We take returns until one week before the concert.",
				typesLabel: "Which ticket types do you take back?",
				typesNotRefundable: "Not taken back: {{names}}",
				typesNoneSelected:
					"Nothing picked — every return request will be refused.",
				typesAllSelected: "Every ticket type is taken back.",
				futureTypes:
					"New ticket types start out excluded — if you add one later, tick it here.",
			},
			validation: {
				nameRequired: "Name is required.",
				venueRequired: "Pick one of your locations as the venue.",
				datesRequired: "Pick a start and end date.",
				invalidTime:
					"{{time}} doesn't exist on that day in the venue's time zone ({{tz}}) — the clocks skip it when they jump forward in spring. Pick a different time.",
				endAfterStart: "The event must end after it starts.",
				maxTicketsMin: "Max tickets per order must be at least 1.",
				refundWindowRequired: "Pick when returns open and close.",
				refundEndAfterStart: "Returns must close after they open.",
				refundPaidBeforeRequired:
					"Pick the purchase time, or switch that option off.",
			},
			generateArtworkEmptyPrompt: "Describe the poster first.",
			generateArtworkRateLimited:
				"Too many requests this hour — try again later.",
			generateArtworkUnavailable:
				"Couldn't generate artwork right now — try again.",
			generateArtworkError:
				"Something went wrong generating artwork — try again.",
			overlayEditTicketType: "Edit ticket type",
			overlayDuplicateTicketType: "Duplicate ticket type",
			overlayNewTicketType: "New ticket type",
		},
		localize: {
			section: "Languages",
			sectionHint:
				"Write the event description and your ticket types once, in whichever language you think in. This drafts them all in the other language at once — you read every version, correct anything you want, and only then is it stored. For a single field, use the translate icon next to that field instead.",
			saveFirstHint:
				"Save your changes first — this reads the description as it currently stands on the event.",
			openButton: "Languages",
			overlayAriaLabel: "Languages for this event",
			title: "Languages",
			intro:
				"This goes out once for the whole event — the event description and every ticket type together — and comes back in every language the shop serves. Nothing is stored until you accept it.",
			nothingToTranslate:
				"There is nothing to translate yet — write a description, or add a ticket type first.",
			subjectDescription: "Event description",
			subjectTicketType: "Ticket type",
			draftButton: "Translate",
			draftingStatus: "Translating this event's copy",
			draftingNote:
				"Writing your words in every language. This takes a moment.",
			reviewIntro:
				"Read every version and correct anything that reads wrong — these are the exact words buyers will see. Nothing is stored until you accept.",
			localeDe: "German",
			localeEn: "English",
			provenanceYours: "Your own words",
			provenanceTranslated: "Translated",
			nameLabel: "Name",
			descriptionLabel: "Description",
			acceptButton: "Accept",
			discardButton: "Discard",
			acceptSuccess: "Every language is stored.",
			acceptFailed:
				"The translations couldn't be stored — nothing was changed. Try again.",
			draftFailed: "The translation didn't come through — try again.",
			rateLimited: "Too many translations this hour — try again later.",
			unavailable:
				"Translations aren't available right now — try again shortly.",
			// The per-field surface: one trigger beside each copy field, opening
			// that field's language versions beside the original.
			triggerAriaLabel: "Languages — {{field}}",
			originalLabel: "Original",
			originalSeparatorNote:
				"A line holding only // divides the original here. Compare the versions by what they say, not by how long they are — the original can say the same thing more than once, and a version says it once.",
			autoNote:
				"Written automatically from the original. If you change the original, every version here is written anew — corrections included.",
			pendingNote: "The other languages are being written right now.",
			readyNote: "The translations have arrived.",
			emptyPlaceholder: "No version of its own yet — readers see the original.",
			tooLong: "At most {{max}} characters — currently {{count}}.",
			fieldSaveFirstHint:
				"Save this field first — the versions are written from the text as it currently stands.",
			// Under a trigger that is there but can do nothing yet: the versions
			// are written from the text as it stands ON the event, so a field
			// nobody has saved words into has nothing to show and nothing to
			// translate.
			fieldNothingStoredHint:
				"Nothing to translate yet — write this field and save it first.",
			// The ask, in the overlay's own footer beside the versions it would
			// replace.
			fieldTranslateButton: "Translate again",
			fieldTranslateRequested:
				"Asked for a fresh translation — the new versions land here in a moment.",
			fieldTranslateRequestFailed: "That request couldn't be sent — try again.",
			// The pass wrote nothing and nothing re-attempts it on its own. The
			// remedy is the ask inside the versions overlay, which is the one
			// gesture that starts another pass over words nobody edited.
			fieldTranslateFailedHint:
				"The other languages weren't written — buyers see this text in your own language for now. Open the languages and ask for another try.",
			fieldStored: "This field's versions are stored.",
			fieldStoreFailed:
				"That version couldn't be stored — nothing was changed. Try again.",
		},
		console: {
			tabOverview: "Overview",
			tabOrders: "Orders",
			tabGuests: "Guests",
			tabEdit: "Edit",
			colDate: "Date",
			colBuyer: "Buyer",
			colTickets: "Tickets",
			colAmount: "Amount",
			colStatus: "Status",
			colCheckedIn: "Checked in",
			statusPaid: "Paid",
			statusAuthorised: "Not yet charged",
			statusRefunded: "Refunded",
			statusCancelled: "Cancelled",
			guestSuffix: "guest",
			// The word beside the order card's messenger tick — one check, two
			// checks, or the bounce. Kept short, so four of them stacked down
			// a list stay a column the eye scans rather than reads. "Sent" and
			// "Delivered" are distinct on purpose: Lettermint accepting a
			// message is not the receiving server accepting it — the same
			// one-tick/two-tick distinction the grammar itself carries.
			deliveryPending: "Sent",
			deliveryDelivered: "Delivered",
			deliveryOpened: "Opened",
			deliveryBounced: "Not delivered",
			detailTicketsLabel: "Tickets in this order",
			detailNoTickets: "No tickets issued yet",
			ticketCheckedIn: "Checked in",
			ticketNotYet: "Not yet checked in",
			ticketCancelled: "Cancelled",
			overviewNoTypesTitle: "No ticket types yet",
			overviewNoTypesBody: "Add one from Edit to start selling.",
			navDashboard: "Dashboard",
			navOrders: "Orders",
			navEdit: "Edit",
			navAnalytics: "Analytics",
			checkinSettingsAriaLabel: "Check-in settings",
			checkinManualLookupHeading: "Find a guest by name",
			checkinEscalateBody:
				"Ask a supervisor — their account can look this guest up by name.",
			back: "Back to events",
			eventNotFoundTitle: "Event not found",
			eyebrowOnSale: "On sale",
			eyebrowPresent: "Happening now",
			eyebrowAfter: "Past event",
			noTicketTypesConfigured: "No ticket types configured yet",
			noAccessBody:
				"Ask an admin to grant you a role with sales-report access.",
			noTicketTypesTitle: "No ticket types yet",
			noTicketTypesBody: "Add one from Edit to start selling.",
			goToEditButton: "Go to Edit",
			exceptionsOpenAria: "Open these orders",
			exceptionRefunded: "{{count}} refunded",
			exceptionAuthorised: "{{count}} not yet charged",
			exceptionCancelled: "{{count}} cancelled on a paid order",
			grossTakenLabel: "Gross taken",
			youKeepLabel: "You keep",
			youKeepAtMost: "at most {{amount}}",
			atMost: "at most",
			youKeepAfter: "after {{vat}} VAT and {{fees}} fees",
			youKeepAfterAtLeast:
				"after {{vat}} VAT · fees in for {{reported}} of {{total}} orders",
			// The headline above this line is the GROSS while no fee is in, so
			// it names what the figure still carries — "after … VAT" would read
			// as a subtraction that has not happened.
			grossTakenIncludes:
				"Includes {{vat}} VAT · transaction fees not reported yet",
			youKeepBreakdownAria: "Show the breakdown",
			grossTakenBreakdownAria: "Show what still comes off",
			authorisedSuffix: "{{amount}} approved by the bank but not yet charged",
			last7dLabel: "Last 7 days",
			momentumUp: "{{count}} more than the week before",
			momentumDown: "{{count}} fewer than the week before",
			momentumFlat: "Same as the week before",
			refundedCancelledLabel: "Refunded / cancelled",
			// Rides the header's own schedule line, between the date and the
			// venue — the one place the countdown is not a second statement of
			// a date already printed beside it.
			scheduleDaysToGo: "in {{count}} days",
			presentLabel: "Checked in",
			attendanceLabel: "Attendance",
			checkedInFraction: "{{redeemed}} / {{valid}}",
			// Retained for checked-in and programme surfaces that still state a
			// capacity fraction; the console's on-sale lead is the scalar below.
			soldFraction: "{{sold}} / {{capacity}}",
			soldLabel: "Sold",
			ticketsIssuedLabel: "Tickets issued",
			ticketsIssuedBreakdownAria: "Show issued-ticket breakdown",
			ticketsIssuedBreakdownDialogAria: "Tickets issued breakdown",
			issuedToGuestsLabel: "Issued to guests",
			// Where sell-through is not the lead it demotes to this scalar on the
			// placing line: a count no reader can mistake for the checked-in
			// fraction standing a hand's width from it.
			soldScalar: "{{count}} sold",
			// Pace is a direction, never a number: a printed seven-day figure
			// above the report's own date picker reads as scoped by it, and a
			// glyph would imply a magnitude the band cannot honestly scale.
			paceUp: "Selling faster than last week",
			paceDown: "Selling slower than last week",
			// The caption OF the arrivals count, not a sentence containing it —
			// the figure leads its own cell.
			arrivals15Label: "arrived in the last 15 min",
			attendanceFinal: "Came through the door",
			arrivals15: "{{count}} arrived in the last 15 min",
			noCheckInsYet: "No check-ins yet.",
			publicShopEmptyTitle: "Nothing is on public sale",
			publicShopEmptyBody:
				"No ticket type here is open to anyone with the link, so the shop has nothing to buy. Open a type below and choose 'Anyone with the link'.",
			typesTitle: "Ticket types",
			todayLabel: "Today",
			colType: "Type",
			colPrice: "Price",
			colRevenue: "Revenue",
			colGuest: "Guest",
			noOrdersYet: "No orders yet",
			sellThroughAria: "{{sold}} of {{capacity}} sold",
			// The header ring is a control, so its name says what OPENS, not
			// what it is — the same rule the report's pressable figures follow.
			headlineOpenAria: "{{sold}} of {{capacity}} sold — open ticket types",
			headlineTypeAria: "{{name}} {{sold}} of {{capacity}}",
			closeProgrammeAria: "Close ticket types",
			loadingFigures: "Loading this event's figures",
			ordersLoadFailed: "Could not load the orders — reload the page.",
			notLive: "Reconnecting — this may be out of date.",
			ticketsLoadFailed: "Could not load the tickets — reload the page.",
			capacityAria: "{{capacity}} capacity",
			soldOutLabel: "Sold out",
			searchLabel: "Search by name, email or ticket number",
			searchAriaLabel: "Search orders by name, email or ticket number",
			// Rendered instead of the pair above for a session that cannot list
			// this event's tickets — the box would otherwise offer a lookup that
			// can only come back empty.
			searchBuyerLabel: "Search by name or email",
			searchBuyerAriaLabel: "Search orders by name or email",
			facetGuestLabel: "Guest",
			facetSaleLabel: "Sale",
			facetRefundedLabel: "Refunded",
			dateRangeLabel: "Date range",
			filtersButtonLabel: "Filters",
			filtersButtonAriaActive: "Filters — {{count}} active",
			filtersButtonAriaInactive: "Filters",
			noMatchTitle: "No matches",
			noMatchBody: "No order matches that search.",
			revenueBreakdown: {
				dialogAriaLabel: "What you keep, worked out",
				grossLabel: "Gross taken",
				grossExplained: "What buyers paid for tickets, less what was refunded",
				vatLabel: "VAT owed",
				vatSingleRate: "{{rate}}% — already included in the ticket prices",
				vatMixedRate:
					"Already included in the ticket prices — the rate differs by ticket type",
				feesLabel: "Transaction fees",
				feesExplained:
					"What the payment provider kept to process these payments",
				// Qualifiers, not definitions: each renders BESIDE feesExplained,
				// never in place of it, and never in the figure column.
				feesAtLeast: "at least this much so far",
				feesNotReported: "The payment provider hasn't sent them yet",
				keepLabel: "You keep",
				keepAtMost: "at most {{amount}}",
				// Both counts can be exactly 1, and the catalog has no plural
				// machinery — `t()` substitutes and nothing else — so the noun
				// after the count is always the whole set ("of these orders"),
				// which reads at 1 and at N alike.
				keepPending:
					"Fees have yet to come in on {{count}} of these orders, so this figure will still come down.",
				unattributableNote:
					"Refunds on {{count}} of these orders couldn't be matched to ticket lines and are left out of every figure.",
			},
			orderDetail: {
				ticketsPending: "The tickets for this order haven't been issued yet.",
				ticketsCancelled:
					"This order was cancelled before any ticket was issued.",
				invoiceHeading: "Invoice",
				invoiceOpen: "Open invoice",
				invoiceNone:
					"No invoice — this order was cancelled before any ticket was issued.",
				invoicePending:
					"No invoice yet — this order hasn't been given its number yet.",
				transactionFee: "{{amount}} transaction fee",
				// The provider does not give the acquiring fee back when the
				// order is refunded — the one fact about this feature a reader
				// cannot guess, so the refunded order says it outright.
				transactionFeeNotReturned:
					"{{amount}} transaction fee — not returned on refund",
				transactionFeePending: "transaction fee not reported yet",
				feeSuffix: " · {{fee}} fee",
				// The catalog carries no plural machinery, so a counted noun
				// that inflects needs one key per form, chosen at the call site.
				ticketCountOne: "{{count}} ticket",
				ticketCountMany: "{{count}} tickets",
				checkinLabel: "Check in",
				undoCheckinLabel: "Undo check-in",
				undoCancel: "Keep it",
				undoConfirmTitle: "Undo this check-in?",
				undoConfirmBody:
					"{{typeName}} becomes valid again and can be let in a second time. Which door admitted it, and when, is erased for good.",
				sendTicketsAria: "Send tickets",
				sendTicketsNoPermissionAria:
					"You don't have permission to view sales reports",
				sendTicketsAddressLabel: "Email address",
				sendTicketsInvalidEmail: "Enter a valid email address.",
				sendTicketsNoAddress:
					"No email address on file — this order can't be sent to anyone.",
				sendTicketsSubmit: "Send tickets",
				ticketImageDownloadStarted: "Ticket image download started.",
				sendTicketsFailed: "Could not queue the email. Try again.",
				sendTicketsAddressUpdatedSendFailed:
					"Email corrected. Sending failed; try sending again.",
				sendTicketsCorrectSubmit: "Correct & send",
				sendTicketsCancel: "Cancel",
				sendTicketsConfirmTitle: "Correct this order's email?",
				sendTicketsConfirmBody:
					"Tickets go to {{newEmail}}. {{oldEmail}} no longer reaches this order.",
				sendTicketsConfirmSubmit: "Correct & send",
				sendTicketsConfirmCancel: "Keep it",
				copyLinkAria: "Copy the order link",
				noShareableLinkAria:
					"No link to share — this order can't be opened by link yet",
				shareAria: "Share the order link",
				ticketCountSingular: "ticket",
				ticketCountPlural: "tickets",
				purchased: "Purchased {{relative}}",
				issued: "Issued {{relative}}",
				guestTicketSuffix: " · Guest ticket",
				testOrder: "Test order",
				toastEmailQueued: "Email queued — sending shortly.",
				toastLinkCopied: "Order link copied.",
				toastLinkCopyFailed: "Couldn't copy this order's link.",
				toastShareFailed: "Couldn't share this order.",
				toastEmailUpdated: "Email updated — resending to {{email}}.",
				refundAria: "Refund tickets from this order",
				refundExitAria: "Leave the refund step",
				// `cancelled` is an outcome and the operator is owed the CAUSE — a
				// ticket a ledger row names was refunded by that row, one no row
				// names was purged, and one still valid that a row names is money in
				// flight against a seat that still admits at the door.
				ticketRefunded: "Refunded",
				ticketRefundedAmount: "Refunded {{amount}}",
				ticketRefundAlsoCoversSingular: "with 1 other ticket",
				ticketRefundAlsoCoversPlural: "with {{count}} other tickets",
				ticketRefundConfirming: "Waiting for the provider to confirm it",
				ticketRefundUnderWay: "Refund under way",
				ticketRefundUnderWayBody:
					"Nothing has come back yet, and this ticket still admits at the door",
				ticketRefundRefusedBody:
					"The provider has refused so far — see the refund record below",
				ticketRefundFailed: "Refund failed",
				ticketRefundFailedStillAdmits:
					"No money came back, and this ticket still admits at the door",
				ticketRefundFailedBody:
					"No money came back — see the refund record below",
				ticketCancelledNoRefund: "No refund on record — nothing was paid back",
				ticketRefundChecking: "Checking the refund record…",
				refundRefusalRedeemed: "Admitted at the door — can't be refunded",
				refundRefusalCancelled: "Cancelled — no longer refundable",
				refundRefusalClaimed:
					"This ticket is already part of a refund that's under way",
				refundRefusalUnpriced:
					"No price per ticket — this order and its tickets don't match up",
				// The name carries WHICH seat, not only which type: an order of two
				// identical rows answered to one name, so a screen reader heard the
				// same control twice. The card beside it already numbers the seat.
				refundTicketAria:
					"Refund {{typeName}} {{index}} of {{count}} — {{amount}}",
				refundTicketRevokeAria:
					"Void {{typeName}} {{index}} of {{count}} — no money moves",
				// Opens beside the reading that names what happened to this seat's
				// money — the row whose money it moved, and the only place this
				// question is asked.
				refundRecordAria:
					"Open the refund record for {{typeName}} {{index}} of {{count}}",
				refundTicketsUnreadable:
					"This account can't see this event's tickets, so a refund can't show which ones it would cancel or how many seats would come back. It needs the door check-in permission.",
				refundLedgerPending: "Checking refunds already under way…",
				refundEmpty: "Pick the tickets to refund",
				refundSelectAll: "Select all",
				refundSelectNone: "Select none",
				refundSubmit: "Refund {{amount}}",
				refundRevokeSubmit: "Void the selected tickets",
				refundConfirmTitle: "Refund {{amount}}?",
				refundConfirmBodySingular:
					"{{amount}} goes back to {{buyer}}. 1 ticket stops being admitted at the door, and 1 seat goes back on sale. Nothing here undoes it.",
				refundConfirmBodyPlural:
					"{{amount}} goes back to {{buyer}}. {{count}} tickets stop being admitted at the door, and {{count}} seats go back on sale. Nothing here undoes it.",
				refundRevokeConfirmTitle: "Void the selected tickets?",
				refundRevokeConfirmBodySingular:
					"No money moves — {{buyer}} paid nothing for this ticket. 1 ticket stops being admitted at the door, and 1 seat goes back on sale. Nothing here undoes it.",
				refundRevokeConfirmBodyPlural:
					"No money moves — {{buyer}} paid nothing for these tickets. {{count}} tickets stop being admitted at the door, and {{count}} seats go back on sale. Nothing here undoes it.",
				refundToast:
					"Refund started — as the money goes back, the seats go back on sale.",
				refundFailed: "Couldn't start this refund. Please try again.",
				purgeAria: "Erase this order",
				purgeConfirmTitle: "Erase this order for good?",
				purgeConfirmBodySingular:
					"The record itself goes, not just the ticket: this order, its 1 ticket, its link and its stored files are deleted outright — the seat goes back on sale and any voucher used on it can be used again. The invoice number stays used, and nothing here brings any of it back.",
				purgeConfirmBodyPlural:
					"The record itself goes, not just the tickets: this order, its {{count}} tickets, its link and its stored files are deleted outright — the seats go back on sale and any voucher used on it can be used again. The invoice number stays used, and nothing here brings any of it back.",
				purgeConfirmCta: "Erase for good",
				purgeCancel: "Keep it",
				purgeToast:
					"Order erased — {{count}} tickets and their seats are gone.",
				purgeFailed:
					"Couldn't erase this order. It may already be gone, or it may be a paid order, which can't be erased.",
			},
			purgeAllOrders: {
				openAria: "Erase every test order of this event",
				confirmTitle: "Erase all {{count}} test orders for good?",
				confirmBody:
					"All {{count}} test orders of this event, their tickets, their links and their stored files are deleted outright — the seats go back on sale and any vouchers used on them can be used again. Real orders are untouched. The invoice numbers stay used, and nothing here brings any of it back.",
				confirmCta: "Erase all {{count}}",
				cancel: "Keep them",
				toast: "{{count}} test orders erased.",
				partialToast:
					"{{count}} test orders erased, {{failed}} refused — they may already be gone, or not be test orders.",
			},
			// The order's own refund record, rendered by <order-refunds>. Every
			// string here answers one question an operator has at 9am: did the
			// money come back, and if not, what is true right now about the
			// charge, the tickets and the seats. The seven `failure` tokens are
			// never shown — each one's own consequence is, in the words the
			// refund runbook uses.
			orderRefunds: {
				title: "Refunds",
				waitingSingular: "1 refund on this order is waiting for a person.",
				waitingPlural:
					"{{count}} refunds on this order are waiting for a person.",
				settled: "{{amount}} went back on {{date}}",
				settledTicketsSingular: "1 ticket",
				settledTicketsPlural: "{{count}} tickets",
				pending: "{{amount}} has gone back — Revolut has still to confirm it",
				claiming: "{{amount}} is being paid out",
				correction: "Credit note {{number}}",
				originCustomer: "Returned by the buyer",
				originPsp: "Made in Revolut's dashboard",
				retryingHeadline:
					"{{amount}} hasn't gone back yet — Revolut keeps turning it down",
				retryingLadder:
					"The system asks again every 15 minutes and gives up after ten tries. Nothing has changed on this order yet.",
				retryingInsufficientFunds:
					"Revolut says there isn't enough money to pay this refund out. A refund is paid out of your own Revolut account, and nothing here can read its balance — open Revolut's dashboard and look. If it is short, top it up: the system asks again every 15 minutes, ten times in all, and the first attempt after the top-up pays this out on its own. If it isn't short, this is being turned down for another reason and needs a person.",
				failedProviderRefused: "Revolut turned this refund down for good.",
				failedAttemptsExhausted: "The system asked ten times and stopped.",
				failedAttemptsExhaustedLanded:
					"The tries ran out, but the money had already gone back.",
				failedOverpaid: "More money went back than this order can account for.",
				failedRedeemedAfterClaim:
					"A ticket in this refund was admitted at the door after the refund was taken.",
				failedHeadroomLost:
					"This refund stopped fitting what the order can still give back.",
				failedSettleRefused:
					"The money went back, but none of it could be written down.",
				failedUnsettledPastBound:
					"Revolut's own totals still don't show this refund a day later.",
				moneyNothingMoved:
					"Nothing was recorded: {{amount}} is still on this order's total, the tickets still get in at the door, and the seats are still gone.",
				moneyCheckDashboard:
					"An earlier attempt may still have paid out. Look this up in Revolut's dashboard before you do anything else.",
				moneyDoorRecordStands:
					"Nothing was recorded, and the door's record stands — someone came in on a ticket this refund tried to void. This needs a decision, not another try.",
				moneySettleRefused:
					"{{amount}} left Revolut and this order shows none of it: its total, its tickets and its seats are all unchanged.",
				moneyOverpaid:
					"{{amount}} is recorded — those tickets are void and their seats are back. A further {{unrecorded}} left Revolut and is in no total anywhere.",
				moneyUnsettled:
					"The books already count it: {{amount}} is off this order's total, the tickets are void and the seats are back. If the money never left, all three are wrong.",
				moneyLanded:
					"{{amount}} did go back, and the tickets and seats followed. Only this row's own status is left to clear.",
				holdsTicketsSingular:
					"This row keeps its 1 ticket to itself — no new refund can reach it while the row stands.",
				holdsTicketsPlural:
					"This row keeps its {{count}} tickets to itself — no new refund can reach them while the row stands.",
				clearedByHand:
					"Clearing a row is a correction to the books, made by hand — the refund runbook has the order to do it in.",
				failedUnknown: "This refund stopped, and didn't say why.",
				providerSaid: "Revolut said:",
				copyReference: "Copy the Revolut reference",
				referenceCopied:
					"Reference copied — search for it in Revolut's dashboard.",
				referenceCopyFailed: "Couldn't copy the reference.",
			},
			refundAllOrders: {
				openAria: "Refund every paid order of this event",
				dialogAriaLabel: "Refund every paid order",
				title: "Refund all {{count}} paid orders?",
				moneyLine: "{{amount}} goes back to the buyers",
				ticketsLine: "{{count}} tickets stop being admitted at the door",
				seatsLine: "{{count}} seats go back on sale",
				staysPublished:
					"The event stays published, and those seats can be bought again the moment they come back. If the event is cancelled, archive it as well — this refund doesn't do that for you.",
				redeemedWarning:
					"{{count}} of these orders hold a ticket the door already admitted. Those orders are left alone — refunding one would erase the record of who came in — so their money and their seats are not counted above.",
				ticketsUnreadable:
					"This account can't see this event's tickets, so a refund can't show how many would stop being admitted or how many seats would come back. It needs the door check-in permission.",
				submit: "Refund all {{count}} orders",
				sending: "Sending…",
				sendingAria: "Sending the refunds",
				failed:
					"Couldn't start this refund. Nothing was sent — the orders are untouched.",
				retry: "Try again",
				progressAria: "Orders processed in this refund",
				progressLabel: "{{done}} of {{count}} orders processed",
				progressFailed: "{{count}} refused — check the refund rows",
				progressSettled: "Every order has been processed.",
				progressUnwatched:
					"This account can no longer follow this refund's progress — it keeps running in the background, but the numbers above won't update.",
			},
		},
	},
	ticketTypes: {
		form: {
			kindTicket: "Ticket",
			kindAddon: "Add-on",
			whatIsThisLabel: "What is this?",
			typeHint:
				"A ticket grants entry to the event; an add-on is an extra, like parking or a drink.",
			nameLabel: "What's it called?",
			nameHint:
				"This is what buyers see at checkout — e.g. 'Frühbucher' or 'Day Pass'.",
			priceLabel: "Price per ticket (€)",
			vatLabel: "VAT %",
			vatHint:
				"Usually 7 for event tickets. Not everything gets the lower rate — workshops, for example, often don't.",
			capacityLabel: "How many of these can you sell?",
			capacityHint: "Once this many are sold, it's sold out.",
			claimableByLabel: "Who can get this one?",
			claimableByBuyer: "Anyone with the link",
			claimableByStaff: "Only your team",
			claimableByBuyerHint:
				"It sits in the public shop — anyone with the event link can buy it.",
			claimableByBuyerFreeHint:
				"It sits in the public shop at €0 — anyone with the event link can claim one without paying, until the capacity above runs out.",
			claimableByStaffHint:
				"It never appears in the public shop. Only your team can issue it, from the guest list.",
			claimableByStaffFreeHint:
				"It never appears in the public shop — that's where a free ticket type stays unless you pick 'Anyone with the link'. Only your team can issue it, from the guest list.",
			claimableByCode: "Only with a code",
			claimableByCodeHint:
				"It only appears in the shop once someone enters a code that reveals it — until then nobody sees it.",
			claimableByCodeUnlockedBy: "Revealed by: {{codes}}.",
			claimableByCodeStranded:
				"No code on this event currently reveals it — nobody can buy it.",
			claimableByCodeCreate: "Create a code that reveals it",
			verifyAtDoorLabel: "At the door",
			verifyAtDoorScan: "Scan and go",
			verifyAtDoorCheck: "Someone checks first",
			verifyAtDoorScanHint:
				"Scanning the ticket is enough — the guest walks straight in, and nobody at the door checks anything else.",
			verifyAtDoorCheckHint:
				"Every ticket of this type waits at the door until a staff member confirms it — even when the scanner is set to Auto.",
			doorNoteLabel: "What should staff check? (optional)",
			doorNoteHint:
				"Name the one thing staff should look at before letting someone in — e.g. 'a valid press pass'. That's exactly what they'll read on their screen while the ticket waits.",
			descriptionLabel: "Anything buyers should know? (optional)",
			descriptionHint:
				"Shown under the type's name in the shop, and in full when a buyer taps it — leave empty and nothing changes.",
			entryWindowLabel: "When does this ticket let people in?",
			entryWholeEvent: "The whole event",
			entryCertainDays: "Certain days only",
			entryWholeEventHint:
				"Lets people in on every day of the event — change the event's dates and entry follows along.",
			entryDaysGroupAriaLabel: "Days this ticket lets people in",
			// The whole pill read as one sentence: the chip stacks weekday, day
			// and month as three separate lines, and read out one by one they
			// are three fragments rather than a date.
			entryDayAriaLabel: "Entry on {{day}}",
			// Spoken only while a tap is outstanding — over a settled span it
			// would read as "nothing is picked" with that span lit underneath.
			entryPickFirstDay: "Pick the first day",
			entryPickLastDay:
				"Pick the last day, or tap the same day again for one day only",
			// Each clock names its own day: day chips beside a bare 10:00–16:00
			// read as those hours on every lit day, while what is stored is
			// first-day 10:00 until last-day 16:00, which admits at 03:00 in
			// between.
			entryFromLabelOnDay: "Entry opens · {{day}}",
			entryToLabelOnDay: "Entry closes · {{day}}",
			// The unfenced pair, where there are no days to offer and each field
			// carries its own day already.
			entryFromLabel: "Entry opens",
			entryToLabel: "Entry closes",
			entrySummaryOneDay: "Entry on {{day}}, {{from}}–{{to}} ({{tz}}).",
			entrySummarySpan:
				"Entry from {{start}}, {{from}} until {{end}}, {{to}} ({{tz}}).",
			// The same two sentences without the zone clause, for a type composed
			// before its event exists: no venue resolves yet, so a zone here would
			// be the catalog's default rather than this event's own.
			entrySummaryOneDayNoZone: "Entry on {{day}}, {{from}}–{{to}}.",
			entrySummarySpanNoZone:
				"Entry from {{start}}, {{from}} until {{end}}, {{to}}.",
			salesAfterEntryWarning:
				"Note: sales stay open after entry has ended — for that stretch, this sells a ticket for a day that's already over.",
			salesWindowLabel: "When can people buy this?",
			salesFollowEvent: "Sales follow the event",
			ownSalesWindow: "This ticket has its own sales window",
			salesFollowEventHintDraft:
				"Sales follow the event's own dates — nothing to maintain here.",
			fallbackWindowLabel: "It follows the event's own sales window.",
			inheritedWindowLabel: "on sale from now until the event ends, {{date}}",
			inheritedWindowUntilEntryEnd:
				"on sale from now until entry ends, {{date}}",
			salesFollowEventSuffix:
				"— change the event's dates and this follows along. Nothing to maintain here.",
			salesWindowPlaceholder: "Pick when sales open and close",
			ownWindowHint:
				"These dates are this ticket's alone — the event's own dates no longer apply to it.",
			releaseLabel: "Is it shown right away?",
			releaseAlways: "Available right away",
			releaseAfterSoldOut: "Only after another ticket sells out",
			noChainCandidates:
				"Add another ticket type first — there's nothing to show this one after yet.",
			chainPrompt: "Display after this one sells out:",
			priceDescendsWarning:
				"Heads up: this one costs less than the ticket it shows after — buyers might notice.",
			predecessorClosesAtWarning:
				"Note: the ticket it shows after has its own sales end date — once that passes, this one appears even if it hasn't sold out.",
			whereLabel: "Where does this happen?",
			venueOverrideLabel: "Venue for this ticket",
			useEventVenue: "Use the event's own venue",
			// `<select-location>`'s `emptyHint` — what leaving the override empty
			// MEANS, which is why it states rather than instructs. `useEventVenue`
			// above cannot serve: its German is infinitive-final, the form German
			// uses for a button or option label, so in this slot it would render as
			// a second command rather than an explanation.
			venueEmptyHint: "Uses the event's own venue.",
			templateOverrideLabel: "Look of this ticket",
			useEventTemplate: "Use the event's own template",
			duplicate: "Duplicate",
			// The copy's prefilled name — the suffix marks which row is the new
			// one until the operator retitles it.
			duplicateName: "{{name}} (duplicate)",
			deleteConfirmTitle: "Delete this ticket type?",
			saveFailed: "Could not save the ticket type — try again.",
			deleteFailed: "Could not delete the ticket type — try again.",
			// <delete-refusal>'s ticket-type copy. TicketTypeSchema carries no
			// archived state, so closing the sales window is the gesture that
			// exists; a refunded ticket still blocks, because its order line goes
			// on naming this type long after the money is back.
			deleteRefusalTitle: "This ticket type can't be deleted",
			deleteRefusalLead:
				"It has already been sold — every order, invoice and ticket for it takes its name and its terms from here:",
			deleteRefusalTicketsOne: "1 ticket carries this type",
			deleteRefusalTicketsOther: "{{count}} tickets carry this type",
			deleteRefusalTicketsRemedy:
				"A refunded ticket still counts — its order keeps naming this type long after the money is back.",
			deleteRefusalConsumedOne: "1 of its seats is already taken",
			deleteRefusalConsumedOther: "{{count}} of its seats are already taken",
			deleteRefusalConsumedRemedy:
				"A taken seat comes back only when the order that took it is erased from the order list.",
			deleteRefusalRemedy:
				"Stop selling it instead: set its sales end to now and nobody can buy it again, while everything already sold keeps its name and its price.",
			// The sold-consequence confirm on save. It refuses nothing: the money
			// on a placed order is frozen on OrderLine.unitPrice, and settle
			// re-reads capacity live, so an edit cannot move settled money. The
			// name line is the one that is not obvious — receipt and correction
			// render read the live ticket type, so a rename rewrites the line
			// description on every fiscal document not yet rendered.
			soldConfirmTitleOne: "1 ticket of this type is already sold",
			soldConfirmTitleOther: "{{count}} tickets of this type are already sold",
			soldConfirmPriceOne:
				"1 ticket is already sold at {{oldPrice}}. It keeps that price; new buyers pay {{newPrice}}.",
			soldConfirmPriceOther:
				"{{count}} tickets are already sold at {{oldPrice}}. They keep that price; new buyers pay {{newPrice}}.",
			soldConfirmCapacityBelow:
				"The new capacity of {{capacity}} is below the {{sold}} already sold. Nobody loses a ticket — this type simply stops selling.",
			soldConfirmCapacity:
				"Capacity moves from {{oldCapacity}} to {{newCapacity}}, leaving {{remaining}} still to sell.",
			soldConfirmName:
				"Invoices and credit notes use this type's name at the moment they are created, not when the order was placed. If you rename it from “{{oldName}}” to “{{newName}}”, every invoice or correction still to come for tickets already sold will show the new name; documents already sent keep the old one.",
			validation: {
				nameRequired: "Give it a name first.",
				vatRange: "VAT must be between 0 and 100.",
				priceNegative: "Price can't be negative.",
				capacityInteger: "Capacity must be a whole number, 0 or more.",
				predecessorRequired: "Pick which ticket this one shows up after.",
				salesWindowRequired:
					"Pick when sales open and close, or let it follow the event.",
				salesWindowOrder: "The sales window must close after it opens.",
				entryDaysRequired:
					"Pick the days this ticket lets people in — or set it to the whole event.",
				entryWindowOrder: "The entry window must close after it opens.",
				invalidTime:
					"{{time}} doesn't exist on that day in the venue's time zone ({{tz}}) — the clocks skip it when they jump forward in spring. Pick a different time.",
			},
		},
		// The day-clone sheet — copies one day's whole ticket-type lineup onto
		// other days. The chain-break notice names, before submit, any source-day
		// type whose showAfterSoldOut points outside the day being copied —
		// mirroring chainOptions' own "exclude before commit" precedent rather
		// than a silent post-hoc count.
		dayClone: {
			dayTriggerAriaLabel: "Duplicate {{day}}'s ticket types",
			nothingToCopy:
				"This day has no single-day ticket type to copy — a multi-day pass already admits on every day it covers.",
			triggerAriaLabel: "Duplicate days",
			title: "Duplicate days",
			sourceDayLabel: "Which day do you want to copy?",
			sourceDayEmptyHint:
				"No ticket type is confined to a single day yet — there's nothing to copy.",
			changeSourceDayAriaLabel: "Pick a different day",
			summaryLine:
				"{{count}} ticket types will be copied: {{names}}. Entry shifts to each day, {{hours}} ({{tz}}).",
			chainBreakWarningOne:
				"{{names}} would otherwise only appear once a ticket type outside this day sells out — the copy appears right away.",
			chainBreakWarningOther:
				"{{names}} would otherwise only appear once a ticket type outside this day sells out — the copies appear right away.",
			targetDaysLabel: "Onto which days?",
			targetDaysRequired: "Pick days",
			submitLabel: "Create {{count}} ticket types",
			cloneFailed: "Could not copy the ticket types — try again.",
		},
	},
	vouchers: {
		nav: "Codes",
		list: {
			title: "Codes",
			intro:
				"A code takes money off a basket, reveals a ticket type the shop hides, or both.",
			new: "New code",
			empty: "No codes on this event yet.",
			loading: "Loading codes",
			loaded: "{{count}} codes loaded",
			loadFailed: "Could not load the codes — try again.",
			usedOf: "{{used}} of {{limit}} used",
			percentAll: "{{value}} % off everything",
			percentOn: "{{value}} % off {{types}}",
			percentOnUnlocked: "{{value}} % off it",
			percentOnly: "{{value}} % off",
			fixedAll: "{{value}} off everything",
			fixedOn: "{{value}} off {{types}}",
			fixedOnUnlocked: "{{value}} off it",
			fixedOnly: "{{value}} off",
			unlocks: "Reveals {{types}}",
			unlocksUnnamedOne: "Reveals 1 hidden ticket type",
			unlocksUnnamedOther: "Reveals {{count}} hidden ticket types",
			validUntil: "Valid until {{when}}",
			expiredOn: "Expired on {{when}}",
			usedUp: "Used up",
			copyAria: "Copy code {{code}}",
			copied: "Code {{code}} copied.",
			copyFailed: "Couldn't copy code {{code}}.",
		},
		form: {
			codeLabel: "Code",
			codeHint:
				"4–32 characters, A–Z, 0–9 and hyphen. The code is the name — it can't be changed later.",
			codeInvalid: "4–32 characters, A–Z, 0–9 and hyphen only.",
			generate: "Generate",
			generatedNote:
				"One-off code generated — total uses set to 1 and per person to 1. You can change both below.",
			labelLabel: "What is this code for?",
			labelHint: "Yours only — buyers never see this text.",
			jobHeading: "What the code does",
			jobNone:
				"A code has to do at least one of these — take money off, or reveal a ticket.",
			discountTitle: "Take money off",
			unlockTitle: "Reveal hidden tickets",
			percent: "Percent",
			fixed: "Fixed amount",
			percentLabel: "Percent off the ticket price",
			fixedLabel: "Amount in euros",
			amountEmptyHint: "Leave empty if the code should take nothing off.",
			typeScope: "Only on these ticket types.",
			chainScope:
				"More ticket types appear only once these sell out — a code scoped here stops applying the moment they do.",
			chainAdd: "Add them too",
			unlockHint: "Nothing selected means the code reveals nothing.",
			unlockEmpty:
				"There is no hidden ticket type yet. Set a type's visibility to 'Only with a code' and it will appear here.",
			hundredHeadline: "100 % is not a discount.",
			hundredBody:
				"A free ticket is its own €0 ticket type that this code reveals — not a price discounted to nothing. On the invoice those are two different supplies, and only one of them goes through. Create the type at €0, set its visibility to 'Only with a code', and pick it below under 'Reveal hidden tickets'.",
			hundredAction: "Clear the amount and reveal instead",
			hundredDismiss: "Back to the field",
			validityHeading: "Validity",
			usageLimit: "Total uses",
			perBuyerLimit: "Uses per person",
			perBuyerHint: "Leave empty for unlimited.",
			validUntil: "Valid until",
			noExpiry: "No expiry — the code stands until its uses run out.",
			expiresAt:
				"Expires on {{when}} ({{zone}}). After that the code is refused.",
			expiryImpossible:
				"{{when}} does not exist in {{zone}} — the clocks jump over it. Pick another time.",
			redemptions: "Redeemed",
			redemptionsLoading: "Reading who redeemed this code",
			redemptionsNone: "Not redeemed yet.",
			redemptionUnknownBuyer: "Order {{orderId}}",
			redemptionCount: "{{count}}×",
			usedOf: "{{used}} of {{limit}} used",
			cancel: "Cancel",
			save: "Create code",
			saveExisting: "Save",
			saveFailed: "Could not save the code — try again.",
			deleteConfirmTitle: "Delete this code?",
			deleteConfirmMessage:
				"Anyone still holding it is refused from then on. Orders that already used it keep their discount.",
			deleteFailed: "Could not delete the code — try again.",
		},
	},
	locations: {
		common: {
			withinPrefix: "within {{name}}",
		},
		list: {
			countSingular: "location",
			countPlural: "locations",
			addAriaLabel: "Add location",
			loadingAriaLabel: "Loading locations",
			errorTitle: "Could not load locations",
			errorBody:
				"Check your connection — the location list will reload automatically.",
			deniedTitle: "You don't have access to the location list",
			deniedBody:
				"Ask an admin to grant you a role with event-management access.",
			emptyTitle: "No locations yet",
			emptyBody:
				"An event needs a venue before it can be created — add the first one.",
			addFirst: "Add your first location",
			detailsAriaLabel: "{{name}}, open location details",
			openInMapsAriaLabel: "Open {{name}} in Google Maps",
			usedByOne: "used by {{count}} event",
			usedByOther: "used by {{count}} events",
			unused: "unused",
		},
		form: {
			saveUnconfirmed:
				"Not confirmed yet — the venue may still appear. Save again if it doesn't.",
			nameLabel: "Name",
			addressLabel: "Address",
			addressHint:
				"The full postal address — it's what goes on the calendar invite and the wallet pass",
			timezoneLabel: "Timezone",
			timezoneHint:
				"Every time you enter for this venue is read in this time zone",
			websiteLabel: "Website or maps link",
			notesLabel: "Entrance notes",
			notesHint: "Stage door, ring the bell…",
			withinLabel: "Within (this is a room inside…)",
			withinPlaceholder: "No parent venue",
			noParentVenue: "No parent venue",
			usedByWarningOne: "Used by {{count}} event — remove them first.",
			usedByWarningOther: "Used by {{count}} events — remove them first.",
			deleteConfirmTitle: "Delete this location?",
			saveFailed: "Could not save the location — try again.",
			deleteFailed: "Could not delete the location — try again.",
			// <delete-refusal>'s location copy. usedByWarning* above counts only
			// events; these three rows are the full census the door reads,
			// including the two references the FE guard cannot see — a ticket
			// type's room override and a child room's own parent.
			deleteRefusalTitle: "This venue can't be deleted",
			deleteRefusalLead:
				"It is still in use — ticket PDFs, calendar invites and wallet passes all take their address from here when they're created:",
			deleteRefusalEventsOne: "1 event happens here",
			deleteRefusalEventsOther: "{{count}} events happen here",
			deleteRefusalEventsRemedy:
				"Move an event from its own Schedule & venue card.",
			deleteRefusalTicketTypesOne: "1 ticket type sends people to this room",
			deleteRefusalTicketTypesOther:
				"{{count}} ticket types send people to this room",
			deleteRefusalTicketTypesRemedy:
				"Change the room on the ticket type that points here.",
			deleteRefusalChildLocationsOne: "1 room sits inside it",
			deleteRefusalChildLocationsOther: "{{count}} rooms sit inside it",
			deleteRefusalChildLocationsRemedy:
				"Move a room out using its own Within field.",
			deleteRefusalRemedy:
				"Move each of those somewhere else first — once nothing uses this location, it can be deleted.",
			overlayEdit: "Edit location",
			overlayNew: "New location",
		},
		selectLocation: {
			defaultPlaceholder: "Pick a venue",
			noLocationsYet: "No locations yet.",
			noSpecificVenue: "No specific venue",
		},
	},
	members: {
		common: {
			nameLabel: "Name",
			roleLabel: "Role",
			eventAccessLabel: "Event access",
			permissionsLabel: "Permissions",
			permissionsCountOne: "{{count}} permission",
			permissionsCountOther: "{{count}} permissions",
			builtInLabel: "Built-in",
		},
		header: {
			newRole: "New role",
			invite: "Invite",
		},
		list: {
			loadingAriaLabel: "Loading members",
			errorTitle: "Could not load members",
			errorBody:
				"Check your connection — the member list will reload automatically.",
			deniedTitle: "You don't have access to the member list",
			deniedBody: "Ask an admin to grant you a role with member-list access.",
			emptyTitle: "No members yet",
			emptyBodyCanInvite: "Invite your first team member to get started.",
			emptyBodyCannotInvite:
				"Members are added by your operator — there's no one here yet.",
			inviteSomeone: "Invite someone",
			memberSingular: "member",
			memberPlural: "members",
			pendingLabel: "pending",
			pendingBadge: "Pending",
			roleGroupAriaLabelOne:
				"{{role}} role, {{count}} member, open role details",
			roleGroupAriaLabelOther:
				"{{role}} role, {{count}} members, open role details",
			pendingInviteAriaLabel: "Pending invitation for {{name}}",
		},
		detail: {
			closeAriaLabel: "Close member detail",
			scopeLabel: "Event access",
			grantedLabel: "Granted",
			grantedByOn: "Granted by {{name}} on {{date}}",
			initialProvisioning: "the initial setup",
			formerTeamMember: "a former team member",
			removeButton: "Remove member",
			changeRoleConfirmTitle: 'Change role to "{{roleName}}"?',
			changeRoleConfirmMessage: "Their permissions will update immediately.",
			changeRoleConfirmText: "Change role",
			accessChangedRoleFailed:
				"Your access changed — this role can no longer be edited from here.",
			roleUpdateFailed: "Couldn't update the role — try again.",
			removeConfirmTitle: "Remove this member?",
			removeConfirmMessage:
				"They will lose access to this workspace immediately.",
			removeConfirmText: "Remove",
			accessChangedRemoveFailed:
				"Your access changed — this member can no longer be removed from here.",
			removeFailed: "Couldn't remove the member — try again.",
			roleLockSelf: "You can't change your own role.",
			soleOwnerLockReason:
				"An organization must always have at least one Owner.",
			removeLockSelf: "You can't remove yourself from the workspace.",
			scopeAllEvents: "All events",
			scopeEventsOne: "{{count}} event",
			scopeEventsOther: "{{count}} events",
		},
		invite: {
			closeAriaLabel: "Close invitation form",
			emailLabel: "Email",
			emailPlaceholder: "name@company.com",
			allEventsChip: "All events",
			specificEventsChip: "Specific events",
			selectAtLeastOne: "Select at least one event.",
			coversAllEvents: "Covers all current and future events.",
			send: "Send invitation",
			sending: "Sending…",
			sentToast: "Invitation sent to {{email}}",
			sendFailed: "Couldn't send the invitation — try again.",
		},
		inviteDetail: {
			closeAriaLabel: "Close invitation detail",
			pendingLabel: "Pending invitation",
			sentOn: "Sent {{date}}",
			allEventsCurrentFuture: "All events · current and future",
			resendButton: "Resend invitation",
			resendButtonCooldown: "Resend invitation ({{seconds}}s)",
			resendingLabel: "Sending…",
			revokeButton: "Cancel invitation",
			revokingLabel: "Cancelling…",
			revokeConfirmTitle: "Cancel this invitation?",
			revokeConfirmMessage:
				"This cancels the invitation — the link will stop working.",
			revokeConfirmText: "Cancel invitation",
			revokeConfirmCancelText: "Keep",
			revokeHint: "Removes this pending invitation — the link stops working.",
			resendFailed: "Couldn't resend the invitation — try again.",
			resentToast: "Invitation resent",
			revokeFailed: "Couldn't cancel the invitation — try again.",
			editFailed: "Couldn't update the invitation — try again.",
			editedToast: "Invitation updated",
			expired: "Expired",
			expiresInHoursOne: "Expires in {{count}} hour",
			expiresInHoursOther: "Expires in {{count}} hours",
			expiresTomorrow: "Expires tomorrow",
			expiresInDaysOne: "Expires in {{count}} day",
			expiresInDaysOther: "Expires in {{count}} days",
		},
		role: {
			namePlaceholder: "Role name",
			builtInNotice: "Built-in — cannot be edited",
			editLabel: "Edit",
			toggleEditAriaLabel: "Edit this role",
			closeAriaLabel: "Close role detail",
			grantedCount: "{{count}} granted",
			affectedOne: "1 member affected",
			affectedZero: "No members yet",
			affectedOther: "{{count}} members affected",
			deleteButton: "Delete role",
			deleteConfirmTitle: "Delete this role?",
			deleteConfirmMessage:
				"No members use this role. This action cannot be undone.",
			deleteBlockedOne: "1 member uses this role — reassign them first.",
			deleteBlockedOther:
				"{{count}} members use this role — reassign them first.",
			saveConfirmTitle: "Save changes to this role?",
			saveConfirmMessageOne:
				"This changes the role for the 1 member carrying it. Their access updates immediately.",
			saveConfirmMessageOther:
				"This changes the role for all {{count}} members carrying it. Their access updates immediately.",
			createFailed: "Couldn't create the role — try again.",
			saveFailed: "Couldn't save the role — try again.",
			deleteFailed: "Couldn't delete the role — try again.",
		},
		selectRole: {
			createNewRole: "Create new role",
			viewGrantsAriaLabel: "View what {{role}} grants — {{permissions}}",
		},
		permissionPicker: {
			toggleDomain: "All {{domain}} permissions",
			domainEvents: "Events",
			domainOrders: "Orders",
			domainGuests: "Guest tickets",
			domainCheckin: "Check-in",
			domainReports: "Reports",
			domainMembers: "Members",
			domainGroupAriaLabel: "{{domain}} permissions",
			allGranted: "all granted",
			someGranted: "some granted",
			noneGranted: "none granted",
		},
		overlay: {
			memberAriaLabel: "Member — {{name}}",
			inviteAriaLabel: "Invite a member",
			inviteDetailAriaLabel: "Pending invite — {{name}}",
			roleAriaLabel: "Role — {{name}}",
		},
	},
	ticketTemplates: {
		variant: {
			editorial: "Editorial",
			stencil: "Stencil",
			strata: "Strata",
		},
		walletPreview: {
			appleTriggerLabel: "Preview Apple Wallet pass — {{variant}}",
			googleTriggerLabel: "Preview Google Wallet pass — {{variant}}",
			appleOverlayAriaLabel: "Apple Wallet pass preview — {{variant}}",
			googleOverlayAriaLabel: "Google Wallet pass preview — {{variant}}",
			appleNote:
				"Every row on the pass is a row on the ticket card, in the ticket card's own words: the same Date, Venue, Ticket and Ticket no. labels, and the same date line, written once in the venue's time zone and shown as is. The pass shows your logo at the very top, the one place Apple draws a mark, and nothing beside it: the mark is the brand, so a name next to it would say the same thing twice, and the ticket card's own \"Produced by\" caption stays at the card's foot where it belongs. Which file is used follows the background — on the dark looks it is the wallet-pass version Organization settings makes from your logo, on the light one your own file, because the wallet-pass version is made light and would vanish there. Where neither is available the pass shows your name in that spot instead. Where the event name sits depends on the look: when the strip already spells it as a wordmark, no text row repeats it; otherwise Apple draws the name over the strip, with no label, because the card's wordmark has none. What comes from the template: the background, text and label colors, the strip image, the pass icon — a plain plate in the pass's own background color, never your logo — and every label and value in the buyer's language. Apple draws the rest — the row layout, the capital-letter labels, the type sizes, the smaller ticket details under the date and venue, and the white plate behind the barcode. There is no back of the pass: nothing is sent that the card does not show, so the venue's postal address never leaves the system.",
			googleNote:
				"Every row on the pass is a row on the ticket card, in the ticket card's own words: the same Date, Venue, Ticket and Ticket no. labels, and the same date line, written once in the venue's time zone. The two rows are pinned — Google is told exactly which cell goes where — with one difference we cannot remove: Google hangs the right-hand cell of each row on the right, where the card hangs both on the left. Google's own venue field is not used at all, because it cannot carry a venue name without also carrying a postal address, and the card shows no address. The brand sits at the top as the issuer, which is where Google puts it — the ticket card carries it at the foot. What else comes from the template: the pass background color, the issuer logo, and the barcode value. Which file that is follows the background, the same way an Apple pass picks it — on the dark looks the wallet-pass version Organization settings makes from your logo, on the light one your own file, because the wallet-pass version is made light and would vanish there; Google fetches that very file, the one shown here. Where a dark look has no wallet-pass version yet, this preview shows the logo itself, which Google accepts only as a PNG or JPEG — a WebP file, or no file at all, is left off and Google draws its own generic placeholder. Google draws whichever file it gets twice: as the wide card title this preview stands for, and again in a second slot it asks for square and masks into a circle, where a wide wordmark is cropped to a disc — a crop nothing here can show. No banner image ships: Google places one under the pass's rows, and the ticket card has no such section. Google still owns the type sizes, the text color (which it picks from the background rather than reading from us) and the barcode plate.",
		},
	},
	sale: {
		common: {
			eventPickerLabel: "Event",
		},
		guestIssue: {
			title: "Issue guest tickets",
			subtitle:
				"Free tickets for partners and press — they count against capacity just like a sale.",
			loadingAriaLabel: "Loading guest tickets",
			noEventsTitle: "No events yet",
			noEventsBody:
				"Add an event first — guest tickets need one to issue against.",
			noTypesTitle: "No ticket types yet",
			noTypesBody:
				"This event has no ticket types — add one before issuing a guest ticket.",
			nameLabel: "Recipient name",
			emailLabel: "Recipient email",
			emailPlaceholder: "name@gallery.com",
			localeLabel: "Ticket language",
			typeLabel: "Ticket type",
			qtyLabel: "Qty",
			addRowButton: "Add another ticket type",
			removeRowAriaLabel: "Remove this ticket type",
			submitButton: "Issue tickets",
			submitting: "Issuing…",
			pendingLabel: "Issuing tickets to {{name}}…",
			settledTitle: "Tickets issued",
			settledBody:
				"Issued to {{name}} ({{email}}) — the ticket email is on its way.",
			issueAnotherButton: "Issue another",
			refusedTitle: "Couldn't issue these tickets",
			refusedBodySoldOut:
				"One of the selected ticket types has sold out since you started — pick a different type or a smaller quantity.",
			refusedBodyGeneric: "Something went wrong — try again.",
			tryAgainButton: "Try again",
			writeFailed: "Could not issue the tickets — try again.",
			issuedCountLabel: "{{count}} issued",
			searchLabel: "Search by name, email or ticket number",
			searchAriaLabel: "Search guest orders by name, email or ticket number",
			searchBuyerLabel: "Search by name or email",
			searchBuyerAriaLabel: "Search guest orders by name or email",
			addGuestButton: "Add guest",
			noAccessBody:
				"Ask an admin to grant you a role with sales-report access.",
			noGuestOrdersTitle: "No guest tickets issued yet",
			noGuestOrdersBody:
				"Add a guest to issue the first free ticket for this event.",
			noMatchTitle: "No matches",
			noMatchBody: "No guest order matches that search.",
			mfaRequiredBody:
				"This account needs two-step sign-in turned on before it can issue or see guest tickets.",
			addDialogAriaLabel: "Add a guest",
			nameRequiredError: "Enter the guest's name",
			emailRequiredError: "Enter an email",
			emailInvalidError: "Enter a valid email",
			freeLabel: "Free",
			// The storefront's own words for the same verdict, on the admin side of
			// it: one issuance is one order, and one order mints its tickets in one
			// Firestore transaction, so past {{cap}} the stepper stops.
			capReached: "More than {{cap}} won't go in one go.",
			// Two doors onto one overlay, announced one after the other: the icon
			// offers more information, the name offers itself in full.
			typeRowDescriptionAriaLabel: "More information about {{name}}",
			typeRowNameAriaLabel: "{{name}} — show in full",
			typeRowDescriptionBackLabel: "Back",
			incrementAriaLabel: "One more {{name}}",
			decrementAriaLabel: "One fewer {{name}}",
			qtyFieldAriaLabel: "Quantity of {{name}}",
		},
	},
	checkin: {
		list: {
			// The rest of this list's frames (loading/error/empty title) reuse
			// admin.events.list.* verbatim — same rows, same catalog. Only the
			// empty body differs: the admin grid's own emptyBody invites adding
			// the first event, an action this fallback landing's own actor
			// (tickets.checkin.scan alone) cannot take.
			emptyBody:
				"Ask an event manager to add one before you can check anyone in.",
		},
	},
	settings: {
		title: "Settings",
		nav: {
			organization: "Organization",
			integrations: "Integrations",
			legal: "Legal documents",
		},
		organization: {
			sectionSite: "Website",
			siteOrigin: "Public website URL",
			mapsApiKey: "Google Maps browser key",
			seoTitle: "Search result title",
			seoDescription: "Search result description",
			seoLocale: "Website language",
			socialImage: "Social preview image URL or Storage path",
			favicon: "Browser icon URL or Storage path",
			appleTouchIcon: "Home screen icon URL or Storage path",
			siteInvalid:
				"Check the website URL and image paths. Use an HTTPS website URL without a path.",
			title: "Organization",
			subtitle:
				"Your brand and legal identity — as it appears on the storefront and every receipt.",
			sectionBrand: "Brand",
			brandName: "Brand name",
			brandOrg: "Organization",
			brandOrgHint:
				"The operating organization, when different from the brand name.",
			brandSeed: "Brand color",
			brandSeedHint:
				"The main color — the shop's whole color scheme is built from it.",
			logo: "Logo",
			logoHint:
				"A one-color logo on a see-through background, about 1500×326 in proportion. Only its shape is used — a full-color image or a solid background comes out as a flat silhouette.",
			logoPreviewLightAriaLabel: "Logo preview on a light background",
			logoPreviewDarkAriaLabel: "Logo preview on a dark background",
			logoChooseButton: "Upload logo",
			logoChangeButton: "Change logo",
			logoRemoveButton: "Remove logo",
			logoTooLargeError:
				"This image is larger than 2 MB — choose a smaller file.",
			logoUnsupportedTypeError: "Only JPG, PNG, or WEBP images are supported.",
			walletMarkVerdictDerive:
				"This logo is dark, and on the dark looks wallet passes draw it on a dark background. A light version will be made for those looks — your own file is stored unchanged.",
			walletMarkVerdictColor:
				"This logo is dark and carries color. On the dark looks wallet passes draw it on a dark background, where it will barely show — but making it light would drop its color.",
			walletMarkVerdictTwoTone:
				"This logo is dark with lighter parts cut out of it. On the dark looks wallet passes draw it on a dark background, where it will barely show — but making it light would fill the cut-outs in.",
			walletMarkVerdictKeep:
				"This logo reads well on the dark looks, where a wallet pass draws it on a dark background. The light look draws its pass on a light background and uses this same file as it is, where it may not read.",
			walletMarkVerdictUnreadable:
				"This file could not be read as an image — choose a different one.",
			walletMarkVerdictBlank:
				"This image has nothing visible in it — choose a different one.",
			walletMarkVerdictUnmeasurable:
				"This browser could not read the image to check it. Try another browser, or a different file.",
			walletMarkChoiceOriginal: "Keep mine",
			walletMarkChoiceLight: "Use the light version",
			walletMarkMissingNote:
				"This logo has no wallet-pass version yet, so on the dark looks a wallet pass falls back to your own file, which barely reads there — and where that file is a WebP, neither Apple nor Google accepts it and the pass shows no logo at all.",
			walletMarkGenerateButton: "Make the wallet-pass version",
			walletMarkArmedNote:
				"The wallet-pass version will be made from this logo when you save.",
			walletMarkGenerateFailed:
				"Your changes were saved, but the wallet-pass version could not be made from this logo. Try again, or upload the logo afresh.",
			sectionLegal: "Legal identity",
			legalName: "Legal name",
			address: "Address",
			vatId: "VAT ID",
			register: "Register",
			representative: "Represented by",
			contactEmail: "Contact email",
			contactEmailHint:
				"The address buyers and authorities reach a human through (§ 5 DDG) — different from the sender email below, which is where automated emails come from.",
			phone: "Phone",
			phoneHint: "Optional — a second rapid-contact channel (§ 5 DDG).",
			sectionEmail: "Email",
			emailSender: "Sender email",
			emailSenderHint: "The From address ticket and receipt emails send from.",
			emailSenderPlatformHint:
				"The platform’s address. Connect your own Lettermint account under Integrations to send from your own.",
			requiredFieldMessage: "This field is required.",
			emailValidationMessage: "Enter a valid email address.",
			licence: {
				active: "Licensed until {{until}}.",
				grace: "Licence ended on {{ended}}. Ticket sales stop on {{stops}}.",
				readOnly:
					"Licence ended on {{ended}}. Ticket sales paused since {{stopped}}.",
				missing: "No valid licence is installed. Ticket sales are paused.",
			},
			publicNote:
				"This information is public — every buyer sees it on the storefront and on every invoice.",
			readOnlyNote:
				"Your role doesn't include managing the organization's identity. Ask an owner or admin for access.",
			saveFailed: "Couldn't save the changes — try again.",
			saveSuccessToast: "Organization identity updated.",
		},
		legal: {
			title: "Legal documents",
			subtitle:
				"Your own Terms of Service and Privacy Policy — until published, the platform's default text applies.",
			tabTerms: "Terms of Service",
			tabPrivacy: "Privacy Policy",
			charCount: "{{count}} / {{max}} characters",
			previewLabel: "Preview",
			publishButton: "Publish",
			publishSuccessToast: "Published.",
			publishFailed: "Publish failed — try again.",
			publishNoAnswer:
				"No answer yet — this may still have published. Check before publishing again.",
			formatFailed: "Formatting failed — try again.",
			formatNoAnswer: "Formatting didn't answer in time — try again.",
			readOnlyNote:
				"Your role doesn't include publishing legal documents. Ask an owner or admin for access.",
			pasteHint:
				"Any language, as you have it. We'll detect the language and prepare both German and English drafts for you to review.",
			sourceTextLabel: "Source text",
			formatAndTranslateButton: "Format & translate",
			writeManuallyButton: "Write both languages myself instead",
			formattingNote:
				"Formatting and translating your document — this can take up to a minute, no need to reload.",
			localeLabelDe: "German",
			localeLabelEn: "English",
			provenancePastedByYou: "Pasted by you",
			provenanceTranslatedByAi: "Translated by AI",
			editSourceLabel: "Edit source",
			previewAriaLabel: "Preview ({{locale}})",
			editAriaLabel: "Edit source ({{locale}})",
			platformSectionNote:
				"Every Privacy Policy also includes a Recipients & processors section maintained by the platform. You can't edit or remove it — it updates on its own whenever this shop connects a new service, and it already appears at the end of both documents below.",
			platformSectionDiscloses: "Currently lists: {{vendors}}.",
			advisoryReadyHeading: "Ready to publish",
			advisoryReady:
				"Publishing now replaces the platform's default text with yours, in both languages.",
			advisoryHeadingLocales: "Missing text",
			advisoryHeadingVersion: "Automatic version",
			advisoryLocalesBothEmpty:
				"German and English text are both empty. If you publish now, no reader will see real text in either language — the page will be blank until you add some.",
			advisoryLocaleDeEmpty:
				"German text is empty. If you publish now, German readers won't see any real text — only English readers will see the document you've written.",
			advisoryLocaleEnEmpty:
				"English text is empty. If you publish now, English readers won't see any real text — only German readers will see the document you've written.",
			advisoryVersionBlank:
				"Your document doesn't state a version of its own, so the day you entered it — {{date}} — will be recorded as the version. Change it on either language pane if the document names its own.",
			reviewHeading: "Compliance review",
			reviewNote:
				"An AI check of your own text against what German law requires. It is not legal advice, and it never blocks publishing.",
			reviewSummaryNever: "Not checked yet",
			reviewSummaryRunning: "Checking…",
			reviewSummaryUnavailable: "Check unavailable",
			reviewSummaryClean: "No issues found",
			reviewSummaryFindings: "{{count}} to review",
			reviewBodyNever:
				"Nothing has been checked yet. Start the review whenever you like — it reads both language versions together, and publishing never waits for it.",
			reviewBodyRunning:
				"Both language versions are being checked against what German law requires. This takes up to a minute — you can close this and keep working.",
			reviewBodyUnavailable:
				"The check itself didn't complete, which says nothing about your document. Publishing is unaffected. Try again in a moment.",
			reviewBodyClean:
				"The review read both language versions and raised nothing.",
			reviewStaleNote:
				"Your text has changed since this check ran — everything below describes the earlier version.",
			reviewRunButton: "Run the check",
			reviewRerunButton: "Check again",
			reviewUnchangedToast:
				"Nothing has changed since the last check — the findings still stand.",
			reviewSeverityCritical: "Critical",
			reviewSeverityWarning: "Warning",
			reviewSeverityAdvisory: "Worth tightening",
			reviewSeverityVerify: "For you to confirm",
			reviewEvidenceLabel: "Evidence",
			replaceAriaLabel: "Replace {{kind}} draft with a new paste",
			replaceConfirmTitle: "Replace {{kind}} draft with a new paste?",
			replaceConfirmMessage:
				"This clears the current German and English text for this document. This can't be undone within the editor.",
			replaceConfirmAction: "Replace",
		},
	},
	integrations: {
		// The registry's ids, named in the operator's language. The registry
		// itself (@momo/types' integrations.const.ts) is data with no locale
		// axis; every surface naming an integration reads its title here.
		titles: {
			lettermint: "Email delivery",
			revolutSandbox: "Payments (sandbox)",
			revolutLive: "Payments (live)",
			appleWallet: "Apple Wallet passes",
			googleWallet: "Google Wallet passes",
			gemini: "AI generation",
			metaAds: "Meta ads (Instagram / Facebook)",
		},
		status: {
			notConfigured: "Not configured",
			verifying: "Verifying…",
			verified: "Verified",
			checkFailed: "Check failed ({{reason}})",
			checkFailedDetail: "Check failed ({{reason}}) — {{detail}}",
			saveFailed: "Save failed ({{reason}})",
		},
		// The generic registry-driven row's own authored copy. The field LABELS
		// beside its secret hints come from the registry's own `.describe()`,
		// which states each credential's meaning and carries no locale axis;
		// that fallback row renders only for an id no dedicated card exists for.
		panel: {
			// Each card's Configure/Manage action, named by the integration it
			// opens: seven cards share two visible words, and a screen reader
			// listing the page's buttons would otherwise hear "Configure" five
			// times with nothing to tell them apart.
			configureAriaLabel: "Configure {{name}}",
			manageAriaLabel: "Manage {{name}}",
			readDenied: "You don't have access to the integration status.",
			readError: "Could not load the integration status.",
			verify: "Verify",
			saveAndVerify: "Save & verify",
			secretKeepHint:
				"Leave blank to keep what's already saved — a saved key is never shown again, so an empty field means 'keep it', not 'delete it'.",
			secretWriteOnlyHint: "Once saved, it's never shown again.",
		},
		lettermint: {
			configureButton: "Configure",
			manageButton: "Manage",
			platformMail:
				"Mail goes out from {{sender}} through the platform’s mail account until you connect your own Lettermint account.",
			platformMailUntilSender:
				"Your Lettermint account is connected. Mail still goes out from {{sender}} through the platform’s account until you set your own sender address under Organization.",
			wizardAriaLabel: "Configure Lettermint email delivery",
			form: {
				closeAriaLabel: "Close the Lettermint keys form",
				intro:
					"Lettermint uses two different keys: the project token sends emails, the team token manages your account and delivery tracking. Pasting one where the other belongs is the easiest mistake to make, so each field checks its own prefix as you type.",
				projectTokenLabel: "Project API token",
				projectTokenHint:
					"This key lets the shop send emails — tickets, receipts, sign-in codes. It can't be checked automatically, so it's saved without being checked. If emails stop going out after a save, recheck it here first. You'll find it in your Lettermint dashboard under Project → API tokens.",
				projectTokenKeepHint:
					"Leave blank to keep the project token already saved — it's never shown again, so blank means 'keep it', not 'delete it'. Paste a new one only to replace it; changing the team token alone leaves this one untouched. You'll find it in your Lettermint dashboard under Project → API tokens.",
				projectTokenError:
					"This field needs the token that starts with lm_ — not lm_team_.",
				projectTokenRevealAriaLabel: "Show project token",
				projectTokenHideAriaLabel: "Hide project token",
				teamTokenLabel: "Team API token",
				teamTokenHint:
					"This key manages your Lettermint account — delivery routes and delivery tracking; it never sends emails. It's the one that actually gets checked: if it's wrong or lacks account-management rights, saving fails right away, before anything is stored. You'll find it in your Lettermint dashboard under Team settings → API tokens.",
				teamTokenKeepHint:
					"Leave blank to keep the team token already saved — it's never shown again, so blank means 'keep it', not 'delete it'. The saved one is re-checked on every save anyway. Paste a new one only to replace it. You'll find it in your Lettermint dashboard under Team settings → API tokens.",
				teamTokenError:
					"That's the sending token — this field needs the one that starts with lm_team_.",
				teamTokenRevealAriaLabel: "Show team token",
				teamTokenHideAriaLabel: "Hide team token",
				docsLink: "Where to find these tokens",
				saveButton: "Save & verify",
				saving: "Saving…",
				overwriteConfirm: {
					projectToken: {
						title: "Replace your project token?",
						message:
							"Outgoing emails switch to the new project token right away. The saved team token stays as it is.",
					},
					teamToken: {
						title: "Replace your team token?",
						message:
							"Delivery routes and delivery tracking switch to the new team token right away. Outgoing emails are unaffected — the saved project token stays as it is.",
					},
					both: {
						title: "Replace your Lettermint keys?",
						message:
							"Outgoing emails and delivery tracking use the new tokens right away.",
					},
				},
				overwriteConfirmButton: "Replace",
				verifyErrors: {
					invalid:
						"Lettermint rejected this token — double-check you copied the Team token, not the Project token, from Settings → API keys.",
					notAuthorized:
						"This token isn't allowed to manage delivery tracking — use one with account-management access.",
					unreachable:
						"Lettermint couldn't be reached to verify the team token — check your connection and save again.",
					notConfigured:
						"The tokens weren't at fault — this save was refused over something on this shop that isn't set up yet. An admin completes it under Settings, then save again.",
				},
				transportErrors: {
					unauthenticated:
						"Your session expired — sign in again and save once more.",
					notAuthorized:
						"You're not allowed to manage integration settings — make sure two-step sign-in is on, or ask an admin for access.",
					invalidInput:
						"This save was rejected before anything was stored — reload the page, then paste both tokens and save again.",
					unknown: "Something went wrong saving these keys — try again.",
				},
				route: {
					label: "Delivery route",
					hint: "Which Lettermint route delivers this workspace's status updates.",
					notYetAvailable:
						"Save your tokens first — reopen this dialog afterward to choose which route delivers status updates.",
					loadError:
						"Couldn't load your Lettermint routes — check your connection and try again.",
					retryButton: "Try again",
					empty:
						"No delivery routes found in your Lettermint account yet. Create one there, then reopen this dialog.",
					defaultBadge: "default",
				},
			},
		},
		revolut: {
			confirmation: {
				doneButton: "Done",
				title: "Switch to sandbox",
				consequence: "New checkouts will use test payments.",
				done: "Sandbox is active",
				sendError: "The code could not be sent. Try again.",
				codeError:
					"That code is invalid or expired. Try again or request a new code.",
				saveError: "The switch could not be completed. Try again.",
				sendAgain: "Send code",
			},
			overview: {
				editEnvironment: "Edit {{env}}",
				title: "Revolut",
				unavailable: "Account status unavailable",
				notChecked: "Not checked",
				notConnected: "Not connected",
				platformSandbox: "Sandbox · Platform account",
				ownSandbox: "Sandbox · Your account",
				ownLive: "Live · Your account",
			},
			configureButton: "Configure",
			manageButton: "Manage",
			platformSandbox: "Platform test account (default)",
			wizardAriaLabel: "Configure Revolut {{env}}",
			wizard: {
				envLive: "Live",
				envSandbox: "Sandbox",
				closeAriaLabel: "Close Revolut {{env}} setup",
				intro:
					"Turn on Apple Pay, Google Pay, cards, and Revolut Pay for ticket checkout.",
				prereqNote: "You'll need a Revolut Business account with API access.",
				docsLink: "Get API keys from Revolut",
				secretKeyLabel: "Secret key",
				secretKeyHint:
					"Once saved, this key is never shown again or filled in for you. You need it to set this up.",
				secretKeyKeepHint:
					"Leave blank to keep the key already saved — blank means 'keep it', not 'delete it', so you can change the currency, the publishable key, or which environment is active without typing in a key that's never shown to you. Paste a new key only to replace it.",
				secretKeyBadCharsError:
					"No spaces or line breaks — paste it as one line.",
				secretKeyMissingPrefixError:
					"Must start with sk_ — that's how Revolut issues a secret key.",
				publicKeyLabel: "Publishable key",
				publicKeyHint:
					"Goes with the secret key above — the shop only uses it once you also switch this environment on below.",
				publicKeyKeepHint:
					"Leave blank to keep the publishable key already saved for this environment — blank means 'keep it', not 'delete it'. Paste a new key only to replace it.",
				publicKeyRequiredWhenActivateError:
					"Enter the publishable key too — switching on needs both keys.",
				currencyLabel: "Checkout currency",
				currencyHint:
					"A 3-letter currency code, like EUR — used for both Sandbox and Live.",
				currencyFormatError: "Enter a 3-letter currency code, like EUR.",
				applePayDomainLabel: "Apple Pay domain",
				applePayDomainHint:
					"The web address Apple Pay checks for this shop's checkout — Live only. Already filled in with this shop's own address; change it only if checkout runs somewhere else.",
				applePayDomainFormatError:
					"Just the address, like tickets.example.com — no https:// and no spaces.",
				activeEnvNote:
					"Saving checks and stores this key — it doesn't change which environment checkout currently uses.",
				activateLabel: "Use {{env}} for checkout",
				activateHint:
					"Checkout can only use one of the two at a time — turning this on switches new orders to {{env}} as soon as this key is checked.",
				activateConfirmTitle: "Use {{env}} for checkout?",
				activateConfirmMessage:
					"New orders start using this environment as soon as the key is checked. To switch back, come back here and switch on the other one instead.",
				activateConfirmButton: "Use it",
				saveButton: "Save & verify",
				verifyingLabel: "Verifying…",
				closeButton: "Close",
				overwriteConfirmTitle: "Replace this Revolut secret key?",
				overwriteConfirmMessage:
					"If checkout is using this environment, the new key takes over as soon as it's checked.",
				overwriteConfirmButton: "Replace",
			},
		},
		appleWallet: {
			configureButton: "Configure",
			manageButton: "Manage",
			wizardAriaLabel: "Configure Apple Wallet passes",
			certificateExpiresLabel: "Certificate expires {{date}}",
			wizard: {
				closeAriaLabel: "Close",
				continueButton: "Continue",
				backButton: "Back",
				saveButton: "Save & verify",
				verifyingLabel: "Verifying…",
				closeButton: "Close",
				setup: {
					title: "Setup",
					description: "Four steps, in order",
					copied: "Copied",
					copyFailed: "Couldn't copy — select the text and copy it manually",
					copyAriaLabel: "Copy this command",
					createId: {
						title: "Create the ID at Apple",
						body: "Description: any name. Identifier: pass.de.yourcompany.ticket",
						link: "Open Apple",
					},
					makeRequest: {
						title: "Make the signing request — run this in a terminal",
					},
					getCertificate: {
						title: "Get the certificate from Apple",
						body: "Under Services, pick Pass Type ID Certificate. Choose the ID you just made, upload pass.certSigningRequest, then Download.",
						link: "Open Apple",
					},
					buildFiles: {
						title: "Build the two files — run this in a terminal",
					},
					expiryNote:
						"Apple's certificate lasts one year. Put a reminder in the calendar for month eleven.",
				},
				credentials: {
					title: "Certificate & IDs",
					description: "Paste the two files",
					passTypeIdLabel: "Pass Type ID",
					passTypeIdHint: "The identifier you registered at Apple.",
					teamIdLabel: "Team ID",
					teamIdHint: "Shown next to your company name at Apple.",
					certificatePemLabel: "Certificate",
					certificatePemHint: "Open passcert.pem and paste everything in it.",
					certificatePemKeepHint: "Leave blank to keep the saved one.",
					wwdrPemLabel: "Apple WWDR",
					wwdrPemHint: "Open wwdr.pem and paste everything in it.",
					wwdrPemKeepHint: "Leave blank to keep the saved one.",
				},
				verify: {
					invalidTitle: "This certificate pair didn't sign correctly",
					unreachableTitle: "The check couldn't complete",
					notAuthorizedTitle: "Apple didn't accept this certificate",
					unknownTitle: "Something went wrong",
				},
				transportErrors: {
					unauthenticated: {
						title: "Your session has expired",
						body: "Sign in again and retry — this certificate was never tested.",
					},
					notAuthorized: {
						title: "You don't have permission to save this",
						body: "Saving this needs two permissions: Manage integrations to store the certificate, and Manage events to store the Pass Type ID and Team ID. Ask a workspace admin for whichever you're missing — nothing was saved and this certificate was never tested.",
					},
					invalidInput: {
						title: "This submission couldn't be sent",
						body: "Reload the page and try again — this certificate was never tested.",
					},
					unknown: {
						title: "Something went wrong saving this certificate",
						body: "Try again in a moment — this certificate was never tested.",
					},
				},
				done: {
					title: "Done",
					description: "Stored and verified",
					heading:
						"This certificate pair was tested by signing a real test pass, and it's now saved for this workspace.",
					certificateExpiresLabel:
						"It expires {{date}} — plan to replace it before then.",
				},
				overwriteConfirm: {
					certificate: {
						title: "Replace your Pass Type ID certificate?",
						message:
							"New passes are signed with this certificate as soon as it's checked. The saved Apple WWDR certificate stays as it is.",
					},
					intermediate: {
						title: "Replace the saved Apple WWDR certificate?",
						message:
							"New passes are signed with this Apple WWDR certificate as soon as it's checked. The saved certificate stays as it is.",
					},
					both: {
						title: "Replace your certificate and Apple WWDR certificate?",
						message:
							"New passes are signed with this certificate pair as soon as it's checked.",
					},
				},
				overwriteConfirmButton: "Replace",
			},
		},
		googleWallet: {
			configureButton: "Configure",
			manageButton: "Manage",
			wizardAriaLabel: "Configure Google Wallet passes",
			wizard: {
				closeAriaLabel: "Close",
				continueButton: "Continue",
				backButton: "Back",
				serviceAccountLabel: "This shop's service account",
				copyServiceAccountAriaLabel: "Copy service account email",
				copyServiceAccountSuccess: "Service account email copied.",
				prerequisites: {
					title: "Prerequisites",
					description: "Set up in Google's own console first",
					intro:
						"These two steps happen in Google's own Wallet console, not here. Neither blocks Continue — Verify (next) is what actually proves them, so skipping ahead just means Verify tells you what's still missing.",
					issuerLabel: "A Wallet API issuer exists for this shop",
					issuerWhy:
						'Open "Google Wallet API" in the console\'s left menu — ignore the long "Create an event ticket class" form it opens; this app creates ticket classes itself, one per event. The issuer ID itself is a setting of this shop, not something you type here.',
					developerRoleLabel:
						"This shop's service account has Developer access",
					developerRoleWhy:
						"In the Wallet console, open Users → Invite a user and grant Developer access to the service account this shop runs as in your Google Cloud project{{project}} — Verify (next) shows its exact address. If this shop moves to a new runtime account, invite the new address before it does. This is the most commonly missed step, and it's done in the Wallet console — not in Google Cloud IAM.",
					demoModeNote:
						"Until this issuer has publishing access, a pass only saves to Google accounts listed on that same Users page. Done (after Verify) walks you through requesting publishing access.",
					footerAdvisory:
						"Prerequisites are advisory — Verify (next) is the real check.",
				},
				verify: {
					title: "Verify",
					description: "One live check against Google Wallet",
					idleBody:
						"Not checked yet — this checks this shop's own identity against Google Wallet, live. It only reads, so it's safe to run as often as you like.",
					verifyButton: "Verify",
					noPermission: "You don't have permission to set up integrations.",
					pendingBody: "Checking your setup…",
					passedTitle: "Connected",
					passedBody: "This shop is allowed to create Google Wallet passes.",
					invalidTitle: "No issuer confirmed",
					invalidBody:
						"Google didn't confirm a Wallet API issuer for this shop — either none is set up yet, or the one set up doesn't exist in the console. Here's exactly what Google said:",
					notAuthorizedTitle: "Not authorized yet",
					notAuthorizedBody:
						"Google answered, but this shop isn't allowed to issue passes on this issuer yet. In the Wallet console, open Users → Invite a user and grant this shop's own service account Developer access, then retry. Here's exactly what Google said:",
					unreachableTitle: "Couldn't reach Google Wallet",
					unreachableBody:
						"The request got no response — check your connection and retry. Here's exactly what Google said:",
					unknownTitle: "Something went wrong",
					unknownBody:
						"The check didn't pass, and this outcome has no step written for it. Here's exactly what Google said:",
					copyDetailAriaLabel: "Copy the details",
					copyDetailSuccess: "Details copied.",
					copyDetailError: "Couldn't copy — select and copy the text manually.",
					retryButton: "Retry",
				},
				done: {
					title: "Done",
					description: "Verified — one step before going live",
					heading: "The issuer is verified.",
					publishingAccessTitle: "Publishing access — required before go-live",
					publishingAccessBody:
						"Until this issuer has publishing access, Google saves passes only to accounts you list on the console's Users page and marks every pass [TEST ONLY]. Publishing access lifts both — any Google account can then save a pass, including passes already issued.",
					step1:
						'Open the Google Pay & Wallet console and pick "Google Wallet API" in the left menu.',
					step2:
						"Confirm the two things Google checks for: a complete Business Profile, and at least one Passes Class — this app creates one automatically the first time an order issues a pass.",
					step3:
						"Open the request and describe your use case. The text below answers exactly that — copy it in.",
					useCaseLabel: "Your use-case answer, ready to paste",
					useCase:
						"We are an event ticketing platform. When a buyer completes a purchase, we issue one Google Wallet event ticket pass per ticket bought, and give the buyer a save link on their order page and in their confirmation email.\n\nEach pass carries only what a guest needs at the door: the event name, the venue name and street address, the event start and end date and time, our organisation name and logo as the issuer, and a QR code that our door staff scan to admit the guest.\n\nThe QR code encodes a random token identifying that one ticket in our system. Admission is always decided by that ticket's current status in our own database, never by the pass, so a refunded or cancelled ticket is refused at the door even if the pass on the phone still looks valid.\n\nThe passes contain no personal data: no guest name, no email address, and no payment or order information.",
					copyUseCaseAriaLabel: "Copy the use-case answer",
					copyUseCaseSuccess: "Use-case answer copied",
					reviewNote:
						"Review takes Google 2–3 working days; approval is one-way, so submit when you're ready for real buyers — sooner rather than at go-live.",
					openConsoleButton: "Open the Google Pay & Wallet console",
					smokeTestNote:
						"While the request is pending, add your own Google account to that Users page to test a pass yourself.",
					closeButton: "Close",
				},
			},
		},
		gemini: {
			configureButton: "Configure",
			manageButton: "Manage",
			wizardAriaLabel: "Configure Gemini AI generation",
			form: {
				closeAriaLabel: "Close the Gemini API key form",
				apiKeyLabel: "Gemini API key",
				saveButton: "Save & verify",
				verifyingLabel: "Verifying…",
				closeButton: "Close",
			},
		},
		metaAds: {
			loadingAnnouncement: "Checking the Meta ads connection",
			configureButton: "Configure",
			manageButton: "Manage",
			wizardAriaLabel: "Configure Meta ads",
			form: {
				wizard: {
					title: "Meta",
					accountSavedTitle: "Ad account saved",
					continueVerification: "Continue verification",
					stepSetup: "Get ready",
					stepValues: "Connect",
					stepDone: "Done",
					continueButton: "I have the details",
					setupAgain: "Setup guide",
					replace: "Replace",
					stored: "Saved",
					keepStored: "Keep saved credential",
					scopeLabel: "Use Meta for",
					measurementLabel: "Report purchases",
					adsLabel: "Report purchases and run ads",
					lockedNote:
						"Ads are enabled. Clearing an account ID does not remove it.",
					confirmTitle: "Enable ads?",
					confirmMessage:
						"This ad account stays connected. Future checks also require your legal identity and a funded ad account.",
					confirmButton: "Enable ads",
					discardTitle: "Discard changes?",
					discardButton: "Discard",
					keepEditing: "Keep editing",
					doneTitle: "Meta is connected",
					doneMeasurement: "Paid purchases are reported to your pixel.",
					doneAds:
						"Purchase reporting is connected. You can launch ads from the event console.",
					firstAccountNote:
						"The ad account is saved. Save and verify again to check its billing and your legal identity.",
					prerequisites:
						"You need a business portfolio and a Facebook Page. For Instagram ads, connect a professional Instagram account.",
					businessSettings: "Open Business Settings",
					appTitle: "Create an app",
					appBody:
						"Create a Meta app with the Marketing API use case. Copy its app ID and app secret.",
					appLink: "Create the app",
					portfolioTitle: "Add your assets",
					portfolioBody:
						"In Business Settings, add your app, ad account, Facebook Page and Instagram account to the same portfolio. Copy the IDs you will use for ads.",
					portfolioLink: "Open Business Settings",
					pixelTitle: "Create a pixel",
					pixelBody:
						"In Events Manager, connect a Web data source. Create the pixel and copy its ID.",
					pixelLink: "Open Events Manager",
					tokenTitle: "Generate a System User token",
					tokenBody:
						"In Business Settings → Users → System users, assign the app and assets. Generate a token with the permissions needed for purchase reporting and, if selected, ads.",
					tokenLink: "Open System users",
					wrongToken:
						"Use the System User token from Business Settings. An Events Manager-only token may report purchases but cannot run ads.",
				},
				closeAriaLabel: "Close the Meta ads form",
				credentialsHeading: "Credentials",
				credentialsNote: "Use the token and secret from your Meta app.",
				accessTokenLabel: "Conversions API access token",
				accessTokenHint: "From Business Settings → Users → System users.",
				accessTokenKeepHint: "Replace the saved System User token.",
				appSecretLabel: "App secret",
				appSecretHint: "From the app dashboard, beside the app ID.",
				appSecretKeepHint: "Replace the saved app secret.",
				credentialPasteError:
					"Paste it as one line — no spaces and no line breaks.",
				measurementHeading: "What gets measured",
				measurementNote: "The pixel that receives purchase reports.",
				pixelIdLabel: "Meta Pixel ID",
				pixelIdHint: "From Events Manager → Data sources.",
				pixelIdKeepHint: "Clearing keeps the saved Pixel ID.",
				pixelIdFormatError:
					"Just the number from Events Manager — digits only, nothing else.",
				advertiserHeading: "Where ads run",
				advertiserNote: "The accounts and identities used for ads.",
				appIdLabel: "Meta app ID",
				appIdHint: "From your Meta app dashboard.",
				adAccountIdLabel: "Ad account ID",
				adAccountIdHint: "From Business Settings → Accounts → Ad accounts.",
				adAccountIdFormatError:
					"This one carries Meta’s act_ prefix — act_ followed by the account number. If you are looking at the number on its own, put act_ in front of it.",
				pageIdLabel: "Facebook Page ID",
				pageIdHint: "The Facebook Page in your business portfolio.",
				instagramAccountIdLabel: "Instagram account ID",
				instagramAccountIdHint:
					"From Business Settings → Accounts → Instagram accounts.",
				numericIdFormatError:
					"Digits only — copy the number Meta shows, with nothing around it.",
				saveButton: "Save & verify",
				verifyingLabel: "Verifying…",
				closeButton: "Close",
			},
		},
	},
	analytics: {
		rangeToday: "Today",
		range7d: "7 days",
		range30d: "30 days",
		rangeCustom: "Custom range",
		computing: "updating …",
		asOfCached: "Updated {{time}}",
		asOfFresh: "Updated {{time}}",
		refresh: "Refresh report",
		// The campaign scope, said where it governs. The panel has no name for
		// a campaign — the cost card below is what identifies one — so the chip
		// up here states that a scope is on and hands back the way out.
		campaignScope: "One campaign",
		campaignScopeClear: "Show every campaign again",
		busyAnnouncement: "Updating the report",
		readyAnnouncement: "Report is up to date",
		kpiDaysToGo: "Days to go",
		kpiVisits: "Visits",
		kpiConversion: "Purchase rate",
		kpiRevenue: "Revenue",
		kpiDwell: "Typical visit length",
		kpiReturning: "Returning visitors",
		deltaWithinRange: "vs. the first half of this period",
		trendVisits: "Visits per day",
		trendRevenue: "Revenue per day",
		gapRule: "A visit ends after {{minutes}} minutes of no activity",
		kpiVisitors: "Visitors",
		legendPaidShare: "of those, bought",
		retrievalCollections: "By format",
		retrievalAllVia: "tickets kept, all as {{channel}}",
		funnelTitle: "From basket to ticket",
		landingClickThrough:
			"{{through}} of {{landing}} home-page visits went on to an event page ({{percent}} %)",
		stoppedOnLanding: "{{count}} stopped on the home page",
		stageEventPage: "Basket started",
		stageContact: "Contact details filled in",
		stageAuthorising: "Payment started",
		stagePaid: "Paid",
		droppedOut: "dropped out",
		funnelCoveragePartial:
			"Not all of the data for this period could be read. The counted steps are therefore approximate.",
		// Says only that the steps cannot be established, never which of the four
		// things that produce this word went missing: a scoped day whose payment
		// milestone was read perfectly and which failed only on campaign
		// attribution reads this too, and naming basket data there would be false.
		funnelCoverageUnavailable:
			"These steps cannot be established for part of this period. Nothing is counted here — read them as unknown, never as zero.",
		// The same gap as the line above, on a period where it did not swallow
		// everything: the days that CAN be established are counted, and the
		// count says how many days that was, so the figures below are read as
		// what they are — a shorter period than the one on the picker — rather
		// than as the whole week the range names.
		funnelCoverageCountedDays:
			"Counted over the {{counted}} days of {{total}} that can be established. The rest of this period is not counted here — read those days as unknown, never as zero.",
		// Rides IN the figure column, where `feesAtLeast` deliberately does not:
		// that one is a sentence qualifying a figure whose direction is known, and
		// this is the ordinary one-word prefix a number wears when it is
		// approximate in both directions.
		figureApproximate: "about {{value}}",
		conversionCoverageIncomplete: "cannot be established",
		outcomesTitle: "Why these did not become tickets",
		clientSpecsTitle: "Visitor devices & browsers",
		clientSpecsEmpty:
			"No client details have been measured in this period yet.",
		clientOs: "Operating system",
		clientBrowser: "Browser",
		clientDevice: "Device type",
		clientViewport: "Screen size",
		clientOrientation: "Screen orientation",
		clientFiltersTitle: "Active visitor filters",
		clientFilterClear: "Remove {{filter}}",
		clientCoverageComplete: "Client details are fully covered.",
		clientCoveragePartial: "Some client-filtered metrics are incomplete.",
		clientCoverageUnavailable:
			"Client-filtered metrics are unavailable for part of this answer.",
		clientValues: {
			ios: "iPhone / iPad",
			android: "Android",
			macos: "macOS",
			windows: "Windows",
			linux: "Linux",
			chromeos: "ChromeOS",
			safari: "Safari",
			chrome: "Chrome",
			firefox: "Firefox",
			edge: "Edge",
			samsung: "Samsung Internet",
			phone: "Phone",
			tablet: "Tablet",
			desktop: "Desktop",
			xs: "Extra small",
			sm: "Small",
			md: "Medium",
			lg: "Large",
			xl: "Extra large",
			portrait: "Portrait",
			landscape: "Landscape",
			other: "Other",
		},
		outcomeSoldOut: "Sold out",
		outcomeNotEligible: "Not eligible",
		outcomeInternal: "Technical issue",
		outcomeLicenceReadOnly: "Licence lapsed",
		outcomeAbandoned: "Left in the basket",
		outcomeExpired: "Expired",
		campaignsTitle: "Where the visits come from",
		colChannel: "Channel",
		colVisits: "Visits",
		colCheckouts: "Baskets started",
		colPaid: "Paid",
		colConversion: "Purchase rate",
		channelDirect: "Direct",
		campaignsEmpty: "No visits in this period yet.",
		// ── The paid campaigns: cost beside sales ────────────────────────────
		// A different subject from the channel block above, which counts every
		// visit by the source that sent it and knows nothing about money.
		costTitle: "What the advertising cost",
		costSubtitle:
			"Each campaign's full run, not the date range above. Meta's numbers sit beside what this shop actually saw — never merged into one figure.",
		costSpent: "Spent",
		costPerTicket: "Per ticket sold",
		costPerTicketNone: "nothing sold yet",
		costTicketsSold: "Tickets sold",
		// What a card is CALLED. The contract carries no operator-typed label,
		// so a run is named by when it ran — verb-led, because a bare date span
		// beside the panel's own Today/7d/30d chips reads as a second range
		// control, and a verb is not a filter. The verb distinguishes an active
		// run from a finished one without adding another status control.
		costRunOpen: "Running since {{span}}",
		costRunClosed: "Ran {{span}}",
		// Two of these on one panel collapse again — nothing is left to reach
		// for, and every other field is an opaque id this surface withholds.
		costRunUnstamped: "Start unknown",
		costScopeOnly: "Only this campaign",
		costMetaClicks: "Meta counted {{clicks}} clicks · read {{time}}",
		costObservedVisits:
			"{{visitors}} of them arrived here and {{orders}} of them bought · read {{time}}",
		// The four traffic verdicts. Three of them leave every figure beside
		// them at zero and only one of those three is a fault, so each says
		// which it is — without these lines a quiet campaign and a broken
		// attribution join render as the same empty row.
		trafficUnreported: "No figures from Meta yet",
		trafficUnreportedWhy:
			"Meta hasn't reported yet — the zeros beside it aren't a result.",
		trafficNone: "Nobody has come through yet",
		trafficNoneWhy:
			"The ad has run and nobody has clicked yet — these zeros are accurate.",
		trafficArriving: "Visits are being traced",
		trafficArrivingWhy:
			"Meta's clicks match the visits arriving here — the figures below are reliable.",
		trafficUntraceable: "These clicks are not reaching us",
		trafficUntraceableWhy:
			"Meta counted clicks, but none could be traced to a visit here. The spend is real — treat the figures below as unknown, not zero.",
		retrievalTitle: "How buyers keep their ticket",
		retrievalSubtitle:
			"Which format they saved it in — PDF download, Apple/Google Wallet, calendar file, or handed off to someone else.",
		retrievalDownload: "PDF downloaded",
		retrievalWalletApple: "Apple Wallet",
		retrievalWalletGoogle: "Google Wallet",
		retrievalIcs: "Calendar (.ics)",
		retrievalHandover: "Passed on",
		retrievalEmpty: "Nobody has kept a ticket in this period yet.",
		browserBias:
			"Someone who visits on a phone and on a laptop counts as two visitors.",
		presence: {
			onEvent: "on this event right now",
			elsewhere: "{{count}} elsewhere in the shop",
			nobody: "Nobody in the shop right now",
		},
		drill: {
			openSources: "see the detail",
			crumbOrders: "Orders",
			crumbTickets: "Tickets",
			crumbSources: "Sources",
			crumbLimit: "Limit",
			closeAll: "Close",
			partsHeader: "{{count}} parts",
			partsEmpty: "Nothing to break down in this period",
			openedFrom: "Total",
			partsShort: "Parts add up to {{sum}}, not the total above.",
			attributionParts: "Where this number comes from",
			titleVisitsByWeek: "Visits, by week",
			titleVisitsByDay: "Visits, by day",
			titleVisitsByCampaign: "Visits on {{day}}, by campaign",
			titleRevenueByWeek: "Revenue, by week",
			titleRevenueByDay: "Revenue, by day",
			titleBrowsers: "Visitors, new and returning",
			titleChannel: "{{channel}} performance",
			titleChannelOnDay: "{{channel}} on {{day}}",
			titleDropPayment: "Payment started, never paid",
			titleStageByWeek: "{{stage}}, by week",
			titleStageByDay: "{{stage}}, by day",
			claimVisitsWeeks: "Weekly totals for the selected range.",
			claimVisitsDays: "Daily totals for the selected range.",
			claimVisitsCampaign: "Campaigns plus direct visits make up the full day.",
			claimChannel:
				"Each visit from this channel counts once — as paid, unpaid, or left.",
			claimBrowsers:
				"New and returning visitors add up to the day's total; a visitor active on 3 days counts on each.",
			claimRevenue: "Reflects refunds, so a past day's total can still change.",
			claimDropPayment:
				"Every basket that started payment and didn't complete it falls into one of these.",
			claimStage:
				"Buyer-started baskets that reached this stage, one day per row.",
			titleProvenance: "{{subject}} — where this number comes from",
			provDerivation: "Result",
			provRange: "Range",
			provRangeDays: "{{from}} – {{to}}, {{days}} days",
			provGap: "Gap between visits",
			provScope: "Included",
			provScopeAll: "All campaigns for this event",
			provScopePlus: "{{others}} · {{campaign}}",
			provComputedAt: "Last updated",
			provComputedCached: "{{time}}",
			provComputedFresh: "{{time}}",
			provSourcesTitle: "Based on",
			provSourceOrders: "Paid orders — for revenue.",
			provSourceCacheUsed: "Already-cached days.",
			provSourceCacheUnused:
				"Not used — this gap setting recomputes every day.",
			provFreshnessTitle: "Freshness",
			provFreshnessCount: "{{stored}} of {{total}} days cached",
			provFreshnessAllFresh: "All {{total}} days computed fresh",
			provFreshnessDay: "{{day}} — computed fresh",
			provSensitivityTitle: "With a different visit gap",
			provSensitivityLead: "The same visits, grouped differently.",
			provSensitivityCurrent: "in use",
			provDocuments: "{{count}} records",
			provDocumentsTimeline:
				"{{pageViews}} page views · {{basketChanges}} basket changes · {{orderActions}} tickets kept · {{checkoutStages}} stages reached",
			provDocumentsNone: "Not used",
			provNeighbourTitle: "Why the numbers differ",
			provVisitsClaim: "{{visits}} visits vs. {{visitors}} visitors.",
			provVisitsBecause:
				"A visitor can return more than once a day — each gap starts a new visit. Both numbers are correct; they count different things.",
			provVisitorsClaim: "Visitors are summed across each day in the period.",
			provVisitorsBecause:
				"Someone active on 3 days counts 3 times — this sums daily visitors, not unique people over the period.",
			provDwellClaim:
				"The median visit length per day — not the median across the whole period.",
			provDwellBecause:
				"Individual visit lengths aren't stored, only each day's median — so days can't be pooled into one range-wide figure.",
			provBasketClaim:
				"{{baskets}} baskets vs. {{visits}} visits — baskets can exceed visits.",
			provBasketBecause:
				"A basket starts the moment it's no longer empty. Returning through a saved link can start a new basket without a new visit.",
			provRevenueClaim:
				"{{paid}} paid baskets — revenue can drop from refunds without this number changing.",
			provRevenueBecause:
				"The funnel counts a basket the moment it's paid. A later refund can lower that day's revenue without changing this count.",
			attributionProvenance: "Every figure here follows the same method.",
			doorHowDerived: "Where this number comes from",
			titleOrdersOfDay: "Paid orders on {{day}}",
			titleOrdersOfChannel: "{{channel}} — orders on {{day}}",
			titleTickets: "Tickets from invoice {{invoice}}",
			recordsLeadOrders: "The orders behind the money.",
			recordsLeadTickets: "{{buyer}} · {{total}}",
			recordsBoundOrders: "Showing {{shown}} of {{total}} orders",
			recordsBoundOrdersTop:
				"Showing {{shown}} of {{total}} orders, largest first",
			recordsBoundTicketsOne: "1 ticket, all of it",
			recordsBoundTickets: "{{count}} tickets, all of them",
			recordsOrderDetailOne: "{{tickets}} ticket · invoice {{invoice}}",
			recordsOrderDetail: "{{tickets}} tickets · invoice {{invoice}}",
			recordsStatusPaid: "paid",
			recordsStatusRefunded: "refunded",
			recordsStatusValid: "valid",
			recordsStatusRedeemed: "redeemed",
			recordsAdmitted: "admitted {{time}} · {{door}}",
			recordsNotAdmitted: "not admitted yet",
			recordsDoorUnknown: "door unknown",
			doorTheOrders: "The orders from this day",
			attributionRecords: "Taken from the orders themselves",
			wallPrivacyTitle: "This is as far as it goes",
			wallPrivacyLead:
				"A total opens into a smaller total. It does not open into a person.",
			wallPrivacyRefusal:
				"Visitor counts are anonymous by design — a total can break into smaller totals, but never into one visitor's path through the shop. There's no setting that changes this.",
			wallBoundedTitle: "Folded into one row on purpose",
			wallBoundedLead:
				"Never recorded separately, so there's nothing inside to open.",
			wallBoundedRefusal:
				"Each day keeps its top 20 campaigns by name; everything past that rolls into this row — whether there were 4 more or 4,000.",
			wallDotLabel: "The path ends here",
			wallInstead: "See instead",
			doorSplitByCampaign: "The same number, split by campaign",
			doorNamedCampaigns: "The campaigns listed by name",
			attributionBoundary: "What this report can show, and why",
			partPaid: "Basket started and paid",
			partUnpaid: "Basket started, not paid",
			partLeft: "left without starting a basket",
			partFirstSeen: "first visit that day",
			partReturning: "been before",
		},
		reportFailed: "The report couldn't be computed just now. Try again.",
	},
	// The paid-promotion surface: each event console's own Promotion destination.
	// Sie register throughout,
	// like every other operator-facing string here — the ad copy itself is the
	// buyer's register and is not in this catalog at all: it is composed
	// server-side and stored on the campaign.
	campaign: {
		nav: "Promotion",
		offer: {
			title: "Get more people to your event",
			standing:
				"{{sold}} of {{quota}} tickets sold. Sales run for another {{days}} days.",
			what: "Your event goes on Instagram and Facebook, shown to people nearby. We write the words and make the picture. You see all of it before anything is spent.",
			noSale: "No tickets are on sale, so there is nothing to advertise.",
			notConfiguredTitle: "Advertising isn't set up yet",
			notConfiguredAdAccount:
				"This shop has no ad account yet, so there is nowhere for the ads to run.",
			notConfiguredSpendingLimit:
				"This shop has an ad account, but no spending limit for ads. Nothing can start until one is set.",
			notConfiguredBoth:
				"This shop has neither an ad account nor a spending limit for ads. Both have to be in place before anything can run.",
			notConfiguredWho: "An admin sets that up.",
			notConfiguredCta: "Set up the ad account",
			budgetLabel: "How much do you want to spend?",
			budgetHint: "For the whole run, not per day.",
			ceiling: "This shop allows up to {{amount}} for ads.",
			perDay: "about {{amount}} a day over {{days}} days",
			worth: "about what {{count}} tickets bring in",
			cta: "See the proposal",
		},
		drafting: {
			title: "Building your proposal",
			body: "We write the words and make the pictures from your poster. About half a minute — you can leave this page.",
		},
		failed: {
			title: "We couldn't set the ad up",
			body: "Nothing was spent. Try again.",
			retry: "Try again",
			// One line per member of the failure vocabulary
			// (`campaign-failure.const.ts`). A member exists there because its
			// remedy is somewhere no other member's is, so each line hands over
			// that one step. `unclassified` knows of no cause, so it names none
			// — a guess there sends an operator to re-mint a working token — and
			// for the same reason it may not rule one out either.
			reason: {
				notConfigured:
					"Something is missing under Settings: an ad account, a Facebook Page, a spending limit, or your legal details. An admin adds it, then try again.",
				budgetOutOfBounds:
					"Your amount is under Meta's minimum for this ad account, or over this shop's limit. Try a different amount, or ask an admin to raise the limit.",
				worldMoved:
					"The cheapest ticket changed, so the price in the ad is now wrong. Build a new proposal.",
				creativeUnusable:
					"The picture can't be used. Build a new proposal and pick a different one.",
				deadCredential:
					"Meta no longer accepts this shop's access token. Connect Meta again under Settings, then try again.",
				notPermitted:
					"Meta won't let this token use your ad account or your Facebook Page. Give it access in Meta Business Manager, then try again.",
				adReview:
					"Meta looked at the ad and won't run it. Build a new proposal with different words or a different picture.",
				advertisingBlocked:
					"Meta has stopped this shop from running ads. There is nothing to change in the campaign, the picture or the amount — only Meta can lift this, through their own review.",
				throttled:
					"This ad account has spent everything Meta allows it this hour. Nothing is wrong — try again later.",
				unclassified:
					"Meta refused and didn't say why, so we can't tell you what to change. Try again; if it keeps failing, build a new proposal.",
			},
		},
		// One line per refusal the propose call can answer with, each naming its
		// own remedy — the five are fixed in five different places.
		refusal: {
			nothingPubliclyPurchasable:
				"No ticket is on public sale, so an ad has nowhere to send people.",
			workspaceLegalMissing:
				"Meta shows who paid for every ad. Add your legal details under Settings first.",
			adSpendCeilingUnset:
				"This shop has no spending limit for ads. An admin sets one under Settings.",
			budgetOverCeiling:
				"That is over this shop's limit. Use a smaller amount, or ask an admin to raise the limit.",
			budgetUnderMetaMinimum:
				"Meta needs at least {{amount}} on this ad account.",
		},
		creative: {
			heading: "The picture",
			posterGroup: "Your poster",
			posterGroupWhy: "Already on this event.",
			uploadGroup: "Your own artwork",
			uploadGroupWhy: "Runs exactly as you made it.",
			generatedGroup: "Made here",
			generatedGroupWhy: "From your poster and this event's details.",
			uploadDrop: "Drop your own artwork here, paste it, or click to choose.",
			uploadSending: "Sending {{name}}",
			uploadPreparing: "Preparing {{name}}",
			uploadLanded: "{{name}} is ready to choose.",
			uploadSlow: "This has not arrived.",
			uploadRetry: "Send it again",
			uploadRefused: "We could not read {{name}}. Try a JPEG, PNG or WebP.",
			uploadSmall:
				"{{w}} × {{h}} — narrower than {{min}} px. It will still run, but it may look soft.",
			uploadUnderMetaMinimum:
				"{{w}} × {{h}} — under Meta's minimum of {{min}} px wide, so Meta will not take it. Choose another picture, or send a wider file.",
			aiBadge: "AI-generated",
			aiNote: "AI made this picture. We label it in the ad.",
			uploadNote:
				"Your picture runs without an AI label. If AI made it, saying so is your call.",
			promptSummary: "The text we used",
			promptLede: "Copy it into your own tool, or hand it to your designer.",
			promptCopy: "Copy the text",
			promptCopied: "Text copied",
			none: "No picture yet.",
		},
		proposal: {
			title: "Your proposal",
			lede: "Nothing is running yet. Change anything you like, then release it.",
			staleTitle: "This proposal is from {{date}}",
			staleBody:
				"Prices and dates may have changed since. Check them before you release.",
			copyHeading: "What it says",
			copyBoth: "The ad runs in German and in English.",
			headlineLabel: "Headline",
			bodyLabel: "Text",
			audienceHeading: "Who sees it",
			audienceSentence:
				"People aged {{age}} and over within {{radius}} km of {{place}}.",
			radiusLabel: "Within",
			ageLabel: "From",
			scheduleHeading: "How long",
			scheduleSentence: "From release until {{end}} — {{days}} days.",
			scheduleWhy: "It stops when your last ticket goes off sale.",
			costHeading: "What it costs",
			costWho: "Meta charges your own ad account, not us.",
			confirm: "Release {{amount}}",
		},
		confirm: {
			title: "Release {{amount}}?",
			subtitle:
				"Meta charges your own ad account up to {{amount}} — never more.",
			message:
				"Meta reviews the ad first, usually within a day. Nothing is spent until it runs. You can pause any time; money already spent does not come back.",
			ok: "Release {{amount}} now",
			cancel: "Back",
		},
		status: {
			building: {
				label: "Setting the ad up at Meta",
				body: "Nothing is spent until Meta has reviewed it.",
			},
			submitted: {
				label: "Meta is reviewing the ad",
				body: "Usually within a day. Nothing is spent yet.",
				action: "Withdraw",
			},
			live: {
				label: "Running",
				body: "Your ad is showing on Instagram and Facebook.",
				action: "Pause",
			},
			rejected: {
				label: "Meta did not approve the ad",
				body: "Meta's own words are below. Nothing more will run.",
				action: "End this campaign",
			},
			paused: {
				label: "Paused",
				body: "Nothing more is being spent. The rest of your money is still there.",
				action: "Let it run again",
				// A paused run holds the event's delete door shut, and the refusal
				// there points back here — so ending is offered beside resuming.
				endAction: "End this campaign",
			},
			ended: {
				label: "Finished",
				body: "Your ad has run.",
			},
			spentOf: "{{spent}} of {{total}} spent",
			daysLeft: "{{days}} days left",
			metaSays: "Meta calls this: {{status}}",
			learning: "Meta is still learning who to show it to: {{stage}}",
			window: "Spend counted over Meta's {{window}} window.",
		},
		rejection: {
			nothingSpent: "Nothing was spent.",
			verbatim: "Meta's own words",
		},
		result: {
			tooEarly: "No numbers yet. Meta sends the first ones after a few hours.",
		},
		ad: {
			sponsored: "Sponsored",
			cta: "Get tickets",
		},
		toast: {
			proposeFailed:
				"We couldn't build the proposal just now. Try again in a moment.",
			proposalEditFailed: "Changes could not be saved. Please try again.",
			launchFailed: "The release didn't go through. Nothing was spent.",
			priceFloorMoved:
				"The cheapest ticket has changed, so the price in the ad is wrong. Build a new proposal.",
			proposalStale:
				"This proposal has stood a while, and the event may have moved under it. Build a new one.",
			stopFailed:
				"That didn't reach Meta. Your spend is unchanged — try again.",
			intentDoesNotApply:
				"This campaign isn't in a state where that can be done.",
		},
	},
	onboarding: {
		close: "Back to settings",
		reopen: "Setup guide",
		continue: "Continue",
		skip: "Skip for now",
		noAnswer:
			"No answer yet — this may still have saved. Check before trying again.",
		completed: "Completed: {{steps}}.",
		skipped: "Skipped: {{steps}}.",
		steps: {
			organization: "Organization",
			legal: "Legal identity",
			sender: "Sender",
			payments: "Payments",
			venue: "First venue",
			done: "Done",
		},
		organization: {
			question: "What's your organization called?",
			lead: "Buyers see this name on your shop, tickets and emails.",
			notAdmitted: "This address can't create an organization.",
			alreadyFounded: "This address has already created an organization.",
			unavailable: "Organizations can't be created right now. Try again later.",
			failed: "Couldn't create the organization. Try again.",
			brandNotSaved:
				"Your organization was created, but its brand wasn't saved. Set it in Settings.",
			brandNoAnswer:
				"Your organization was created. Its brand may not have saved yet — check it in Settings.",
		},
		legal: {
			question: "Who sells the tickets?",
			lead: "Printed on every ticket and invoice, and public in your imprint.",
		},
		sender: {
			question: "Connect your mail",
			lead: "Tickets and invoices are emailed from your own address, through your Lettermint account.",
			platform: "Keep the platform’s mail for now",
		},
		payments: {
			notConfigured:
				"Payment notifications have no public address yet. Start the local tunnel and save again.",
			platform: "Platform test account (default)",
			platformNote:
				"No real charge. Connect your live account to take real payments.",
			question: "How will buyers pay?",
			lead: "Checkout runs through your Revolut Merchant account.",
			sandbox: "Your sandbox",
			live: "Live",
			sandboxNote:
				"Test orders with your Revolut sandbox keys. No money moves until you switch to live in Settings.",
			liveNote: "Real orders, paid out to your Revolut Merchant account.",
		},
		venue: {
			question: "Where is your first event?",
			lead: "Buyers find this address on their tickets and in their calendar.",
			add: "Add venue",
			edit: "Edit",
		},
		done: {
			notReady: "{{name}} can't sell yet.",
			checkoutClosed:
				"Checkout stays closed until a payment environment is activated.",
			platformSandbox:
				"Platform test account (default) · no real charge. Connect your live Revolut account in Settings to take real payments.",
			reading: "Checking what’s connected",
			ready: "{{name}} is set up.",
			sandbox:
				"Checkout takes test payments until you switch Revolut to live in Settings.",
			platformMail:
				"Mail goes out from {{sender}} through the platform’s mail account until you connect your own Lettermint account in Settings.",
			createEvent: "Create your first event",
		},
	},
	nav: {
		events: "Events",
		team: "Team",
		locations: "Locations",
		ticketTemplates: "Ticket templates",
		integrations: "Integrations",
		settings: "Settings",
		switchOrganization: "{{name}} — Switch organization",
		accountAndSignOut: "{{name}} — Account and sign out",
		preferences: "Preferences",
		brandPreferences: "{{brand}} — preferences",
		account: "Account",
		signOut: "Sign out",
		signOutConfirmTitle: "Sign out?",
		signOutConfirmMessage: "You'll need to sign back in.",
		signOutFailed: "Sign out failed — try again.",
		// Appended to the plate's accessible name while invitations are open.
		invitationsWaitingOne: "an invitation is waiting",
		invitationsWaitingOther: "{{count}} invitations are waiting",
		shop: "Shop",
	},
	licence: {
		banner: {
			graceTitle: "Ticket sales stop on {{stops}}",
			graceBody: "Renew the licence with Revia to keep selling.",
			readOnlyTitle: "Ticket sales are paused",
			// "Contact Revia" names no channel yet: an owner fact still to supply.
			readOnlyBody:
				"Orders, tickets, invoices and check-in keep working. Contact Revia to renew the licence.",
		},
	},
	shell: {
		noAccessTitle: "No access yet",
		noAccessBody:
			"Your role doesn't unlock any admin area yet. Ask an admin to grant you one.",
	},
} as const;
